<template>
  <!-- Calendar Popup -->
  <div
    v-if="isCalendarVisible"
    class="calendar-popup"
    :class="{
      'calendar-popup--shell': isShellCalendar,
      'calendar-popup--hint': showCalendarResyncHint,
    }"
    @keyup.esc="closeCalendar"
  >
    <div
      class="calendar-content"
      :class="{
        'with-events-panel': showEventsPanel,
        'with-event-form': showEventModal,
      }"
    >
      <div class="calendar-header">
        <h3>Calendar</h3>
        <div class="calendar-header-controls">
          <button
            @click="toggleSharedCalendar"
            :class="['shared-calendar-btn', { active: !showSharedCalendar }]"
            :title="
              showSharedCalendar
                ? 'Switch to Personal Calendar'
                : 'Switch to Shared Calendar (Default)'
            "
          >
            <i :class="showSharedCalendar ? 'fas fa-users' : 'fas fa-user'"></i>
            {{ showSharedCalendar ? 'Shared (All)' : 'Personal (Mine)' }}
          </button>
          <button
            type="button"
            ref="calendarResyncBtn"
            class="menu-btn calendar-resync-btn"
            title="Clear cache and resync"
            aria-label="Clear cache and resync"
            :disabled="refreshingCalendar"
            @click="clearCacheAndResync"
          >
            <i class="fas fa-cloud-download-alt"></i>
          </button>
          <button
            type="button"
            class="menu-btn calendar-refresh-btn"
            title="Refresh calendar. Press and hold to clear the saved edit and reload."
            @pointerdown="startRefreshHold"
            @pointerup="cancelRefreshHold"
            @pointercancel="cancelRefreshHold"
            @contextmenu.prevent
            @click="onRefreshClick"
          >
            <i
              class="fas fa-sync-alt"
              :class="{ 'fa-spin': refreshingCalendar }"
            ></i>
          </button>
          <button
            @click="closeCalendar"
            class="menu-btn"
            title="Close"
          >
            <i class="fas fa-times"></i>
          </button>
          <button
            class="menu-btn home-btn"
            title="Home"
            @click="$router.push('/mhome')"
          >
            <i class="fas fa-home"></i>
          </button>
        </div>
      </div>

      <div class="calendar-main">
        <div class="calendar-section">
          <div class="calendar-nav">
            <button @click="previousMonth" class="menu-btn nav-btn">
              <i class="fas fa-chevron-left"></i>
            </button>
            <h4>{{ formatMonthYear(currentDate) }}</h4>
            <button @click="nextMonth" class="menu-btn nav-btn">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
          <div class="calendar-grid" ref="calendarGrid">
            <div class="calendar-weekdays">
              <div class="weekday">Sun</div>
              <div class="weekday">Mon</div>
              <div class="weekday">Tue</div>
              <div class="weekday">Wed</div>
              <div class="weekday">Thu</div>
              <div class="weekday">Fri</div>
              <div class="weekday">Sat</div>
            </div>
            <div class="calendar-days">
              <div
                v-for="day in calendarDays"
                :key="day.date"
                :class="{
                  'calendar-day': true,
                  'other-month': day.otherMonth,
                  today: day.isToday,
                  selected: day.isSelected,
                }"
                @click="selectDate(day)"
                @dblclick="addEventForDay(day)"
              >
                {{ day.day }}
                <div
                  v-if="getEventCount(day.date) > 0"
                  class="event-badge"
                  :class="{ 'personal-badge': hasPersonalEvents(day.date) }"
                  :style="{ backgroundColor: getEventColor(day.date) }"
                >
                  {{ getEventCount(day.date) }}
                </div>
              </div>
            </div>
          </div>
          <div class="calendar-footer">
            <button @click="goToToday" class="menu-btn">Today</button>
            <button @click="openListView" class="menu-btn">List</button>
            <button v-if="userRole < 3" @click="openGridView" class="menu-btn">
              Grid
            </button>
            <select
              v-model="calendarFilter"
              class="calendar-filter-select"
              title="Filter calendar events"
            >
              <option value="all">All</option>
              <option value="office">Office (O, P)</option>
              <option value="urgent">Urgent (!)</option>
            </select>
            <button @click="closeCalendar" class="menu-btn">Close</button>
          </div>
        </div>

        <!-- Events Panel -->
        <div class="events-panel" :class="{ 'show-panel': showEventsPanel }">
          <div class="events-header">
            <button
              @click="closeEventsPanel"
              class="menu-btn close-events-btn"
              title="Close Events Panel"
            >
              <span>«</span>
            </button>
            <button
              type="button"
              class="date-nav-btn"
              @click="navigateEventDate(-1)"
              title="Previous day"
            >
              <i class="fas fa-chevron-left"></i>
            </button>
            <h4>Events for {{ formatSelectedDate() }}</h4>
            <button
              type="button"
              class="date-nav-btn"
              @click="navigateEventDate(1)"
              title="Next day"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
            <button @click="addEvent" class="menu-btn add-event-btn">
              <i class="fas fa-plus"></i>
            </button>
          </div>
          <div class="events-list">
            <div v-if="selectedDateEvents.length === 0" class="no-events">
              No events today
            </div>
            <div v-else class="event-items">
              <div
                v-for="event in selectedDateEvents"
                :key="event.id"
                class="event-item"
              >
                <div class="event-content" @click="editEvent(event)">
                  <div class="event-time">
                    {{ formatTime(event.start_time) }} -
                    {{ formatTime(event.end_time) }}
                    <i
                      class="fas fa-eye event-visibility-icon"
                      :title="getVisibilityText(event.maskForRole)"
                    ></i>
                  </div>
                  <div class="event-main">
                    <div
                      class="event-color-dot"
                      :style="{ backgroundColor: event.color }"
                      :title="getColorLabel(event.color).title"
                    ></div>
                    <div class="event-text-content">
                      <div class="event-title-row">
                        <span class="event-title">{{ event.title }}</span>
                        <span
                          v-if="event.purpose"
                          class="event-purpose-label"
                          >{{ getPurposeFullLabel(event.purpose) }}</span
                        >
                        <button
                          v-if="event.meeting_url"
                          @click.stop="openMeetingUrl(event.meeting_url)"
                          class="meeting-btn"
                          title="Join Meeting"
                        >
                          <i class="fas fa-video"></i>
                        </button>
                        <button
                          @click.stop="copyCalendarLink(event)"
                          class="calendar-link-btn"
                          title="Copy Calendar Link"
                        >
                          <i class="fas fa-share-nodes"></i>
                        </button>
                      </div>
                      <div
                        v-if="event.description"
                        class="event-description-wrapper"
                      >
                        <div
                          class="event-description"
                          :class="{
                            'description-clamped':
                              !expandedListEvents[event.id],
                          }"
                          v-html="renderMarkDown(event.description)"
                        ></div>
                        <button
                          v-if="isDescriptionLong(event.description)"
                          @click.stop="toggleListDescription(event.id)"
                          class="description-more-btn"
                        >
                          {{ expandedListEvents[event.id] ? 'less' : 'more' }}
                        </button>
                      </div>
                      <div v-if="event.location" class="event-location">
                        <i class="fas fa-map-marker-alt"></i>
                        <span>{{ event.location }}</span>
                        <button
                          @click.stop="openGoogleMaps(event.location)"
                          class="location-btn"
                          title="Open in Google Maps"
                        >
                          <i class="fas fa-external-link-alt"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Event Form Flyout -->
          <div v-if="showEventModal" class="event-form-flyout">
            <div class="event-form-header">
              <h3>{{ editingEvent ? 'Edit Event' : 'Add Event' }}</h3>
              <button @click="closeEventModal" class="menu-btn">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="event-form-body">
              <div class="form-group">
                <input
                  v-model="eventForm.title"
                  type="text"
                  placeholder="Event title *"
                  required
                />
              </div>
              <div class="form-group">
                <textarea
                  v-model="eventForm.description"
                  placeholder="Event description"
                  rows="3"
                ></textarea>
              </div>
              <div class="form-group">
                <input
                  v-model="eventForm.location"
                  type="text"
                  placeholder="Event location"
                />
              </div>
              <div class="form-group">
                <input
                  v-model="eventForm.meetingUrl"
                  type="url"
                  placeholder="Meeting URL (https://zoom.us/j/123456789)"
                />
              </div>
              <div class="form-group">
                <label class="checkbox-label">
                  <input v-model="eventForm.isMultiDay" type="checkbox" />
                  <span>Multi-day event</span>
                </label>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label
                    v-if="eventForm.isMultiDay"
                    style="font-size: 0.8em; color: #666; margin-bottom: 2px"
                    >Start Date</label
                  >
                  <input
                    v-model="eventForm.date"
                    type="date"
                    min="1971-01-01"
                    required
                  />
                </div>
                <template v-if="eventForm.isMultiDay">
                  <div class="form-group">
                    <label
                      style="font-size: 0.8em; color: #666; margin-bottom: 2px"
                      >End Date</label
                    >
                    <input
                      v-model="eventForm.endDate"
                      type="date"
                      :min="eventForm.date || '1971-01-01'"
                      required
                    />
                  </div>
                </template>
                <template v-else>
                  <div class="form-group">
                    <input v-model="eventForm.startTime" type="time" required />
                  </div>
                  <div class="form-group">
                    <input v-model="eventForm.endTime" type="time" required />
                  </div>
                </template>
              </div>
              <div
                v-if="!eventForm.isMultiDay && endTimeBeforeStart"
                class="time-warning"
              >
                <i class="fas fa-exclamation-triangle"></i> End time is before
                start time
              </div>
              <div class="form-group">
                <label>Color</label>
                <div class="color-picker">
                  <div
                    v-for="color in colorOptions"
                    :key="color"
                    :class="{
                      'color-option': true,
                      selected: eventForm.color === color,
                    }"
                    :style="{ backgroundColor: color }"
                    :title="getColorLabel(color).title"
                    @click="eventForm.color = color"
                  >
                    <span class="color-option-letter">{{
                      getColorLabel(color).letter
                    }}</span>
                  </div>
                </div>
              </div>
              <div class="form-group">
                <label class="checkbox-label">
                  <input v-model="eventForm.personal" type="checkbox" />
                  <span>Personal Event (private to me)</span>
                </label>
              </div>
              <div class="form-group" v-if="!eventForm.personal">
                <label for="maskForRole">Visible to:</label>
                <select
                  v-model="eventForm.maskForRole"
                  id="maskForRole"
                  class="form-control"
                  style="
                    width: 80%;
                    padding: 8px;
                    border-radius: 4px;
                    border: 1px solid #ddd;
                  "
                >
                  <option :value="null">Everyone</option>
                  <option
                    v-for="role in userRoles"
                    :key="role.roleId"
                    :value="role.roleId"
                  >
                    {{
                      role.roleId === 1
                        ? role.roleName
                        : role.roleName + ' and above'
                    }}
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label for="eventPurpose">Purpose:</label>
                <select
                  v-model="eventForm.purpose"
                  id="eventPurpose"
                  class="form-control"
                  style="
                    width: 80%;
                    padding: 8px;
                    border-radius: 4px;
                    border: 1px solid #ddd;
                  "
                >
                  <option :value="null">None</option>
                  <option
                    v-for="state in purposeStates.filter((s) => s.available)"
                    :key="state.key"
                    :value="state.key"
                  >
                    {{ state.fullLabel }}
                  </option>
                </select>
              </div>
            </div>
            <div class="event-form-footer">
              <button
                v-if="editingEvent"
                @click="deleteEvent"
                class="menu-btn delete-btn"
              >
                <i class="fas fa-trash"></i> Delete
              </button>
              <button
                v-if="editingEvent"
                @click="copyEvent"
                class="menu-btn copy-btn"
              >
                <i class="fas fa-copy"></i> Copy
              </button>
              <button @click="closeEventModal" class="menu-btn cancel-btn">
                Cancel
              </button>
              <button
                @click="saveEvent"
                class="menu-btn save-btn"
                :disabled="savingEvent"
              >
                <i
                  :class="
                    savingEvent ? 'fas fa-spinner fa-spin' : 'fas fa-save'
                  "
                ></i>
                {{
                  savingEvent ? 'Saving...' : editingEvent ? 'Update' : 'Save'
                }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Event Confirmation Modal -->
    <div
      v-if="showDeleteConfirmModal"
      class="modal-overlay"
      @click.self="closeDeleteConfirmModal"
    >
      <div class="modal-content delete-confirmation-modal">
        <div class="modal-header">
          <h3>Delete Event</h3>
          <button @click="closeDeleteConfirmModal" class="modal-close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="delete-warning">
            <i class="fas fa-exclamation-triangle"></i>
            <div>
              <h4>Are you sure you want to delete this event?</h4>
              <p>This action cannot be undone.</p>
              <p class="warning-text">Event: {{ editingEvent?.title }}</p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeDeleteConfirmModal" class="btn btn-secondary">
            Cancel
          </button>
          <button @click="confirmDelete" class="btn btn-danger">
            <i class="fas fa-trash"></i>
            Delete Event
          </button>
        </div>
      </div>
    </div>

    <!-- Scheduling Grid View Modal -->
    <div v-if="showGridView" class="grid-overlay" @click.self="closeGridView">
      <div class="grid-modal-wrapper">
        <div class="grid-modal">
          <div class="grid-header">
            <div class="grid-header-title">
              <h3>
                {{
                  gridActiveTab === 'events'
                    ? 'Weekly Events'
                    : 'Scheduling Availability'
                }}
              </h3>
              <span class="grid-header-range">{{ gridWeekRangeDisplay }}</span>
            </div>
            <button @click="closeGridView" class="menu-btn">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="grid-tabs">
            <button
              :class="['grid-tab', { active: gridActiveTab === 'events' }]"
              @click="gridActiveTab = 'events'"
            >
              <i class="fas fa-calendar-day"></i> Events
            </button>
            <button
              :class="[
                'grid-tab',
                { active: gridActiveTab === 'availability' },
              ]"
              @click="gridActiveTab = 'availability'"
            >
              <i class="fas fa-clock"></i> Availability
            </button>
          </div>

          <div class="grid-nav">
            <button @click="previousGridWeek" class="menu-btn nav-btn">
              <i class="fas fa-chevron-left"></i>
            </button>
            <input
              type="date"
              v-model="gridDatePicker"
              @change="onGridDatePickerChange"
              class="grid-date-picker"
            />
            <button @click="nextGridWeek" class="menu-btn nav-btn">
              <i class="fas fa-chevron-right"></i>
            </button>
            <button
              v-if="gridActiveTab === 'availability'"
              @click="autoFillWeek"
              class="menu-btn fill-btn"
            >
              <i class="fas fa-fill"></i> Fill
            </button>
            <button
              v-if="gridActiveTab === 'availability'"
              @click="clearWeek"
              class="menu-btn clear-btn"
            >
              <i class="fas fa-eraser"></i> Clear
            </button>
          </div>

          <div class="grid-container">
            <div
              class="grid-weekdays"
              :style="{
                'grid-template-columns':
                  '55px repeat(' + gridColumnCount + ', 1fr)',
              }"
            >
              <div class="grid-time-header">
                <button
                  @click="cycleGridMode"
                  class="grid-mode-btn"
                  :title="'View: ' + gridModeLabel + ' (click to cycle)'"
                >
                  {{ gridModeLabel }}
                </button>
              </div>
              <div
                v-for="(day, index) in gridWeekDays"
                :key="index"
                class="grid-weekday"
                :class="{ 'grid-today': day.isToday }"
                @click="selectGridDay(day)"
                :style="{ cursor: 'pointer' }"
                :title="'Show only ' + day.dayName"
              >
                <div class="grid-day-name">{{ day.dayName }}</div>
                <div class="grid-day-date">{{ day.dateDisplay }}</div>
              </div>
            </div>

            <div class="grid-body">
              <div
                v-for="timeSlot in gridTimeSlots"
                :key="timeSlot.time"
                class="grid-row"
                :style="{
                  'grid-template-columns':
                    '55px repeat(' + gridColumnCount + ', 1fr)',
                }"
              >
                <div class="grid-time-label">{{ timeSlot.display }}</div>
                <div
                  v-for="(day, dayIndex) in gridWeekDays"
                  :key="dayIndex"
                  class="grid-cell"
                  :class="[
                    getSlotClass(day.date, timeSlot.time),
                    { 'grid-today': day.isToday },
                    {
                      'grid-cell-selected':
                        selectedCell &&
                        selectedCell.date === day.date &&
                        selectedCell.time === timeSlot.time,
                    },
                  ]"
                  @click="toggleSlot(day.date, timeSlot.time, $event)"
                  @dblclick.prevent="gridSlotDblClick(day.date, timeSlot.time)"
                  @mouseenter="showSlotTooltip(day.date, timeSlot.time, $event)"
                  @mouseleave="hideSlotTooltip"
                >
                  <span class="slot-text">{{
                    getSlotText(day.date, timeSlot.time)
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Custom tooltip for event slots -->
            <div
              v-if="hoveredSlot && hoveredSlotEvents"
              class="grid-tooltip"
              :style="{
                ...(hoveredSlot.anchorBottom
                  ? { bottom: hoveredSlot.bottom + 'px' }
                  : { top: hoveredSlot.y + 'px' }),
                ...(hoveredSlot.anchorRight
                  ? { right: hoveredSlot.right + 'px' }
                  : { left: hoveredSlot.x + 'px' }),
              }"
            >
              <div
                v-for="(ev, idx) in hoveredSlotEvents"
                :key="idx"
                class="grid-tooltip-event"
              >
                <div
                  class="grid-tooltip-title"
                  v-html="renderMarkDown(ev.title)"
                ></div>
                <div
                  v-if="ev.description"
                  class="grid-tooltip-desc"
                  v-html="renderMarkDown(ev.description)"
                ></div>
                <hr
                  v-if="idx < hoveredSlotEvents.length - 1"
                  class="grid-tooltip-divider"
                />
              </div>
            </div>
          </div>

          <div class="grid-footer">
            <div class="grid-legend">
              <span class="legend-item anything">
                <span class="legend-box"></span> Any
              </span>
              <span class="legend-item maintenance">
                <span class="legend-box"></span> Maint
              </span>
              <span class="legend-item lock-batteries">
                <span class="legend-box"></span> Locks
              </span>
              <span class="legend-item lease-sign">
                <span class="legend-box"></span> Lease
              </span>
              <span class="legend-item showing">
                <span class="legend-box"></span> Show
              </span>
              <span class="legend-item office-hours">
                <span class="legend-box"></span> Office
              </span>
              <span class="legend-item inspection">
                <span class="legend-box"></span> Insp
              </span>
              <span class="legend-item unavailable">
                <span class="legend-box"></span> Unavail
              </span>
              <span class="legend-item reserved">
                <span class="legend-box"></span> Reserved
              </span>
              <span class="legend-item event">
                <span class="legend-box"></span> Event
              </span>
            </div>
            <button @click="closeGridView" class="menu-btn">Close</button>
          </div>
        </div>

        <!-- Grid Event Form Flyout -->
        <div v-if="showGridEventForm" class="grid-event-flyout">
          <div class="event-form-header">
            <h3>{{ editingEvent ? 'Edit Event' : 'Add Event' }}</h3>
            <button @click="closeGridEventForm" class="menu-btn">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="event-form-body">
            <div class="form-group">
              <input
                v-model="eventForm.title"
                type="text"
                placeholder="Event title *"
                required
              />
            </div>
            <div class="form-group">
              <textarea
                v-model="eventForm.description"
                placeholder="Event description"
                rows="3"
              ></textarea>
            </div>
            <div class="form-group">
              <input
                v-model="eventForm.location"
                type="text"
                placeholder="Event location"
              />
            </div>
            <div class="form-group">
              <input
                v-model="eventForm.meetingUrl"
                type="url"
                placeholder="Meeting URL"
              />
            </div>
            <div class="form-row">
              <div class="form-group">
                <input
                  v-model="eventForm.date"
                  type="date"
                  min="1971-01-01"
                  required
                />
              </div>
              <div class="form-group">
                <input v-model="eventForm.startTime" type="time" required />
              </div>
              <div class="form-group">
                <input v-model="eventForm.endTime" type="time" required />
              </div>
            </div>
            <div v-if="endTimeBeforeStart" class="time-warning">
              <i class="fas fa-exclamation-triangle"></i> End time is before
              start time
            </div>
            <div class="form-group">
              <label>Color</label>
              <div class="color-picker">
                <div
                  v-for="color in colorOptions"
                  :key="color"
                  :class="{
                    'color-option': true,
                    selected: eventForm.color === color,
                  }"
                  :style="{ backgroundColor: color }"
                  :title="getColorLabel(color).title"
                  @click="eventForm.color = color"
                >
                  <span class="color-option-letter">{{
                    getColorLabel(color).letter
                  }}</span>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input v-model="eventForm.personal" type="checkbox" />
                <span>Personal Event (private to me)</span>
              </label>
            </div>
            <div class="form-group" v-if="!eventForm.personal">
              <label for="gridMaskForRole">Visible to:</label>
              <select
                v-model="eventForm.maskForRole"
                id="gridMaskForRole"
                class="form-control"
                style="
                  width: 80%;
                  padding: 8px;
                  border-radius: 4px;
                  border: 1px solid #ddd;
                "
              >
                <option :value="null">Everyone</option>
                <option
                  v-for="role in userRoles"
                  :key="role.roleId"
                  :value="role.roleId"
                >
                  {{
                    role.roleId === 1
                      ? role.roleName
                      : role.roleName + ' and above'
                  }}
                </option>
              </select>
            </div>
          </div>
          <div class="event-form-footer">
            <button
              v-if="editingEvent"
              @click="deleteEventFromGrid"
              class="menu-btn delete-btn"
            >
              <i class="fas fa-trash"></i> Delete
            </button>
            <button @click="closeGridEventForm" class="menu-btn cancel-btn">
              Cancel
            </button>
            <button
              @click="saveGridEvent"
              class="menu-btn save-btn"
              :disabled="savingEvent"
            >
              <i
                :class="savingEvent ? 'fas fa-spinner fa-spin' : 'fas fa-save'"
              ></i>
              {{ savingEvent ? 'Saving...' : editingEvent ? 'Update' : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- List View Modal -->
    <div v-if="showListView" class="list-overlay" @click.self="closeListView">
      <div class="list-modal">
        <div class="list-header">
          <div class="list-header-title">
            <h3>Upcoming Events</h3>
            <span class="list-header-range">{{ listHeaderRange }}</span>
          </div>
          <button @click="closeListView" class="menu-btn">
            <i
              :class="
                calendarDefaultView === 'list'
                  ? 'fas fa-calendar-alt'
                  : 'fas fa-times'
              "
            ></i>
          </button>
        </div>
        <div class="list-search">
          <input
            v-model="listViewSearch"
            placeholder="Filter events..."
            class="list-search-input"
            @dblclick="listViewSearch = ''"
          />
          <button
            v-if="listViewSearch"
            @click="listViewSearch = ''"
            class="list-search-clear"
          >
            <i class="fas fa-backspace"></i>
          </button>
          <div class="list-search-actions">
            <button
              class="list-print-btn"
              title="Print list"
              @click="printListView"
            >
              <i class="fas fa-print"></i>
              <span class="list-btn-label">print</span>
            </button>
            <button
              class="list-add-btn"
              @click="addEventFromList"
              title="Add event"
            >
              <i class="fas fa-plus"></i>
            </button>
          </div>
        </div>
        <div class="list-container" ref="listContainer">
          <div v-if="listViewGroups.length === 0" class="no-events">
            No upcoming events in the next 90 days
          </div>
          <div v-else class="list-events">
            <template v-for="group in listViewGroups" :key="group.dateStr">
              <div
                class="list-date-header"
                :data-list-date="group.dateStr"
              >
                {{ formatListDateHeader(group.dateStr, group.events) }}
              </div>
              <div
                v-if="group.events.length === 0"
                class="no-events list-day-empty"
              >
                No events
              </div>
              <div
                v-for="event in group.events"
                :key="event.id"
                class="event-item"
              >
                <div class="event-content" @click="editEventFromList(event)">
                  <div class="event-time">
                    {{ formatTime(event.start_time) }} -
                    {{ formatTime(event.end_time) }}
                    <i
                      class="fas fa-eye event-visibility-icon"
                      :title="getVisibilityText(event.maskForRole)"
                    ></i>
                  </div>
                  <div class="event-main">
                    <div
                      class="event-color-dot"
                      :style="{ backgroundColor: event.color }"
                      :title="getColorLabel(event.color).title"
                    ></div>
                    <div class="event-text-content">
                      <div class="event-title-row">
                        <span class="event-title">{{ event.title }}</span>
                        <span
                          v-if="event.purpose"
                          class="event-purpose-label"
                          >{{ getPurposeFullLabel(event.purpose) }}</span
                        >
                        <button
                          v-if="event.meeting_url"
                          @click.stop="openMeetingUrl(event.meeting_url)"
                          class="meeting-btn"
                          title="Join Meeting"
                        >
                          <i class="fas fa-video"></i>
                        </button>
                        <button
                          @click.stop="copyCalendarLink(event)"
                          class="calendar-link-btn"
                          title="Copy Calendar Link"
                        >
                          <i class="fas fa-share-nodes"></i>
                        </button>
                      </div>
                      <div
                        v-if="event.description"
                        class="event-description-wrapper"
                      >
                        <div
                          class="event-description"
                          :class="{
                            'description-clamped':
                              !expandedListEvents[event.id],
                          }"
                          v-html="renderMarkDown(event.description)"
                        ></div>
                        <button
                          v-if="isDescriptionLong(event.description)"
                          @click.stop="toggleListDescription(event.id)"
                          class="description-more-btn"
                        >
                          {{ expandedListEvents[event.id] ? 'less' : 'more' }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
        <div class="list-footer">
          <span class="list-count">{{ listViewEvents.length }} events</span>
          <div class="list-footer-actions">
            <button
              @click="showViewSettingsPopup = true"
              class="menu-btn"
              title="Calendar view settings"
            >
              <i class="fas fa-cog"></i>
            </button>
            <button @click="closeListView" class="menu-btn">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Default View Popup (first time list is opened) -->
    <div
      v-if="showDefaultViewPopup"
      class="modal-overlay"
      @click.self="dismissDefaultViewPopup"
    >
      <div class="view-settings-modal">
        <div class="modal-header">
          <h3>Default View</h3>
          <button @click="dismissDefaultViewPopup" class="modal-close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>
            Would you like to make <strong>list view</strong> the default when
            you open the calendar?
          </p>
        </div>
        <div class="modal-footer">
          <button @click="dismissDefaultViewPopup" class="btn btn-secondary">
            No thanks
          </button>
          <button @click="setDefaultView('list')" class="btn btn-primary">
            Yes, use list view
          </button>
        </div>
      </div>
    </div>

    <!-- View Settings Popup (gear icon) -->
    <div
      v-if="showViewSettingsPopup"
      class="modal-overlay"
      @click.self="showViewSettingsPopup = false"
    >
      <div class="view-settings-modal">
        <div class="modal-header">
          <h3>Default View</h3>
          <button
            @click="showViewSettingsPopup = false"
            class="modal-close-btn"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>
            Would you like to make
            <strong><i class="fas fa-calendar-alt"></i> calendar view</strong>
            or
            <strong><i class="fas fa-list"></i> list view</strong> the default
            when you open the calendar? You can change it later with the
            <i class="fas fa-cog"></i> gear icon at the bottom of list view.
          </p>
          <div class="view-settings-options">
            <button
              @click="setDefaultView('calendar')"
              :class="[
                'view-option-btn',
                { active: calendarDefaultView === 'calendar' },
              ]"
            >
              <i class="fas fa-calendar-alt"></i> Calendar View
            </button>
            <button
              @click="setDefaultView('list')"
              :class="[
                'view-option-btn',
                { active: calendarDefaultView === 'list' },
              ]"
            >
              <i class="fas fa-list"></i> List View
            </button>
          </div>
        </div>
        <div class="modal-footer">
          <button
            @click="showViewSettingsPopup = false"
            class="btn btn-secondary"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showCalendarResyncHint"
      class="calendar-resync-hint"
      role="note"
      :style="{
        '--calendar-resync-hint-top': calendarResyncHintTop,
        '--calendar-resync-arrow-right': calendarResyncArrowRight,
      }"
      @click="hideCalendarResyncHint"
    >
      <div class="calendar-resync-hint-callout">
        <svg
          class="calendar-resync-hint-arrow"
          viewBox="0 0 40 72"
          aria-hidden="true"
        >
          <polygon points="4,28 20,2 36,28" fill="currentColor" />
          <line
            x1="20"
            y1="24"
            x2="20"
            y2="70"
            stroke="currentColor"
            stroke-width="4"
            stroke-linecap="round"
          />
        </svg>
        <p class="calendar-resync-hint-text">
          click this to re-sync your calendar if it is missing data
        </p>
        <button
          type="button"
          class="calendar-resync-hint-dismiss"
          @click.stop="hideCalendarResyncHintForever"
        >
          don't show this again
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import { marked } from 'marked';
import QRCode from 'qrcode';
import {
  CALENDAR_EVENT_DRAFT_KEY,
  CALENDAR_REFRESH_HOLD_MS,
  clearCalendarEventCache,
  calendarResyncHintSeen,
  markCalendarResyncHintSeen,
  calendarYmd,
  localDateFromYmd,
  formatCalendarDayLabel,
  shiftLocalDay,
  calendarTimeHHMM,
  eventOccursOn,
  calendarChipDate,
  listWindowYmd,
  eventInListWindow,
  groupListEventsByDate,
  resolveCalendarEventDraft,
} from '@/utils/calendarDates';

export default {
  name: 'CalendarTool',
  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    initialMonth: {
      type: Number,
      default: null,
    },
    initialYear: {
      type: Number,
      default: null,
    },
    initialDay: {
      type: Number,
      default: null,
    },
    personalCalendar: {
      type: Boolean,
      default: false, // Default to shared calendar
    },
    initialGridTab: {
      type: String,
      default: null, // 'events' or 'availability' — auto-opens grid view to this tab
    },
  },
  emits: ['close', 'notification', 'confirm'],
  data() {
    return {
      currentDate: new Date(),
      selectedDate: null,
      showEventsPanel: false,
      showEventModal: false,
      showDeleteConfirmModal: false,
      savingEvent: false,
      editingEvent: null,
      events: [],
      showSharedCalendar: !this.personalCalendar, // Use prop to set initial state
      isMobileStandalone: false, // Track if accessed directly via mobile route
      showCalendarResyncHint: false,
      calendarResyncHintTop: '0px',
      calendarResyncArrowRight: '13px',
      // Touch gesture tracking
      touchStartX: 0,
      touchEndX: 0,
      touchStartY: 0,
      touchEndY: 0,
      eventForm: {
        title: '',
        description: '',
        location: '',
        meetingUrl: '',
        date: '',
        startTime: '09:00',
        endTime: '10:00',
        color: '#007bff',
        personal: false,
        maskForRole: 3, // Default to 'user' role (roleId 3)
        purpose: null,
      },
      eventFormDraftTimer: null,
      eventFormDraftSuppressed: false,
      restoringEventFormDraft: false,
      eventsLoadOk: false,
      refreshHoldTimer: null,
      refreshHoldFired: false,
      refreshingCalendar: false,
      colorOptions: [
        '#007bff', // Blue
        '#28a745', // Green
        '#dc3545', // Red
        '#fd7e14', // Orange
        '#6f42c1', // Purple
        '#20c997', // Teal
        '#ffc107', // Yellow
        '#e83e8c', // Pink
      ],
      userRoles: [], // List of user roles from database
      userRole: null, // Current user's role
      // Scheduling Grid View state
      showGridView: false,
      gridActiveTab: 'events', // 'events' or 'availability'
      // List View state
      showListView: false,
      listFocusYmd: null,
      listViewSearch: '',
      expandedListEvents: {},
      // Default view preference
      showDefaultViewPopup: false, // first-time "make list view default?" popup
      showViewSettingsPopup: false, // gear icon settings popup
      calendarDefaultView:
        localStorage.getItem('calendarDefaultView') || 'calendar', // 'calendar' or 'list'
      hasAskedDefaultView:
        localStorage.getItem('calendarDefaultViewAsked') === 'true',
      gridWeekStart: null, // Sunday of the current grid week
      gridDatePicker: '', // Date picker value for jumping to a week
      gridAvailability: {}, // Map of "YYYY-MM-DD_HH:mm" -> { available, purpose, location, reserved_by }
      gridEvents: {}, // Map of "YYYY-MM-DD_HH:mm" -> { isEvent, events: [...] }
      lastClickedCell: null, // Track last clicked cell for shift-click range selection { date, time }
      selectedCell: null, // Currently selected cell { date, time } - clicks cycle purpose only when selected
      lastRangeFill: null, // Stores previous slot states for Ctrl+Z undo of range fill
      hoveredSlot: null, // { date, time, x, y } for custom tooltip positioning
      showGridEventForm: false, // Show event form flyout next to grid
      gridDayFilter: null, // null = all 7 days, 'weekdays' = Mon-Fri, or index 1-5 for single day
      calendarFilter: 'all', // 'all', 'office', 'urgent'
      purposeStates: [
        {
          key: 'unavailable',
          label: '',
          fullLabel: 'Unavailable',
          available: false,
          cssClass: 'slot-unavailable',
        },
        {
          key: 'anything',
          label: 'Any',
          fullLabel: 'Anything',
          available: true,
          cssClass: 'slot-anything',
        },
        {
          key: 'maintenance',
          label: 'Maint',
          fullLabel: 'Maintenance',
          available: true,
          cssClass: 'slot-maintenance',
        },
        {
          key: 'lock_batteries',
          label: 'Locks',
          fullLabel: 'Lock Batteries',
          available: true,
          cssClass: 'slot-lock-batteries',
        },
        {
          key: 'lease_sign',
          label: 'Lease',
          fullLabel: 'Lease Signing',
          available: true,
          cssClass: 'slot-lease-sign',
        },
        {
          key: 'showing',
          label: 'Show',
          fullLabel: 'Showing',
          available: true,
          cssClass: 'slot-showing',
        },
        {
          key: 'office_hours',
          label: 'Office',
          fullLabel: 'Office Hours',
          available: true,
          cssClass: 'slot-office-hours',
        },
        {
          key: 'inspection',
          label: 'Insp',
          fullLabel: 'Inspection',
          available: true,
          cssClass: 'slot-inspection',
        },
      ],
    };
  },
  computed: {
    endTimeBeforeStart() {
      return (
        this.eventForm.startTime &&
        this.eventForm.endTime &&
        this.eventForm.endTime <= this.eventForm.startTime
      );
    },
    // Determine if calendar should be visible
    isCalendarVisible() {
      return this.showModal || this.isMobileStandalone;
    },
    // /calendar always sits in the 440px phone column, including on a wide desktop window.
    isShellCalendar() {
      return (
        this.isMobileStandalone ||
        (this.$route && this.$route.path === '/calendar')
      );
    },
    // Check if currently in mobile mode
    isMobile() {
      return window.innerWidth <= 768;
    },
    calendarDays() {
      const days = [];
      const today = new Date();
      const firstDay = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth(),
        1,
      );

      // Start from Sunday of the week containing the first day
      const startDate = new Date(firstDay);
      startDate.setDate(firstDay.getDate() - firstDay.getDay());

      // Generate 42 days (6 weeks) to fill the calendar grid
      for (let i = 0; i < 42; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);

        const isOtherMonth = date.getMonth() !== this.currentDate.getMonth();
        const isToday = date.toDateString() === today.toDateString();
        const isSelected =
          this.selectedDate &&
          date.toDateString() === this.selectedDate.toDateString();

        days.push({
          date: calendarYmd(date),
          day: date.getDate(),
          otherMonth: isOtherMonth,
          isToday: isToday,
          isSelected: isSelected,
        });
      }

      return days;
    },
    filteredEvents() {
      const officeColors = ['#fd7e14', '#6f42c1'];
      const urgentColor = '#dc3545';
      switch (this.calendarFilter) {
        case 'office':
          return this.events.filter((e) => officeColors.includes(e.color));
        case 'urgent':
          return this.events.filter((e) => e.color === urgentColor);
        default:
          return this.events;
      }
    },
    selectedDateEvents() {
      const selectedDateStr = calendarYmd(this.selectedDate);
      if (!selectedDateStr) return [];
      return this.filteredEvents.filter((event) =>
        this.isEventOnDate(event, selectedDateStr),
      );
    },
    hoveredSlotEvents() {
      if (!this.hoveredSlot) return null;
      const key = this.getSlotKey(this.hoveredSlot.date, this.hoveredSlot.time);
      const ev = this.gridEvents[key];
      if (!ev || !ev.isEvent) return null;
      return ev.events;
    },
    // Grid view computed properties
    gridWeekDays() {
      if (!this.gridWeekStart) return [];
      const days = [];
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

      const today = this.formatDateForKey(new Date());
      for (let i = 0; i < 7; i++) {
        const date = new Date(this.gridWeekStart);
        date.setDate(date.getDate() + i);
        const dateKey = this.formatDateForKey(date);
        days.push({
          dayName: dayNames[i],
          dayIndex: i,
          date: dateKey,
          isToday: dateKey === today,
          dateDisplay: date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          }),
        });
      }

      // Apply day filtering
      if (this.gridDayFilter === 'weekdays') {
        return days.filter((d) => d.dayIndex >= 1 && d.dayIndex <= 5);
      } else if (typeof this.gridDayFilter === 'number') {
        return days.filter((d) => d.dayIndex === this.gridDayFilter);
      }
      return days;
    },
    gridColumnCount() {
      return this.gridWeekDays.length;
    },
    listHeaderRange() {
      const bounds = listWindowYmd(new Date(), this.listFocusYmd, 90);
      const normal = listWindowYmd(new Date(), null, 90);
      if (
        !bounds ||
        !normal ||
        (bounds.startYmd === normal.startYmd &&
          bounds.endYmd === normal.endYmd)
      ) {
        return 'Next 90 days';
      }
      const start = formatCalendarDayLabel(bounds.startYmd);
      const end = formatCalendarDayLabel(bounds.endYmd);
      if (!start || !end) return 'Next 90 days';
      return `${start} – ${end}`;
    },
    listViewEvents() {
      const bounds = listWindowYmd(new Date(), this.listFocusYmd, 90);
      let events = this.filteredEvents
        .filter((event) => eventInListWindow(event, bounds))
        .sort((a, b) => {
          const dateA = `${calendarYmd(a.event_date)}T${
            calendarTimeHHMM(a.start_time) || '00:00'
          }`;
          const dateB = `${calendarYmd(b.event_date)}T${
            calendarTimeHHMM(b.start_time) || '00:00'
          }`;
          if (dateA < dateB) return -1;
          if (dateA > dateB) return 1;
          return 0;
        });

      if (this.listViewSearch.trim()) {
        const term = this.listViewSearch.trim().toLowerCase();
        events = events.filter((e) => {
          const title = (e.title || '').toLowerCase();
          const desc = (e.description || '').toLowerCase();
          return title.includes(term) || desc.includes(term);
        });
      }

      return events;
    },
    listViewGroups() {
      const focus = this.listViewSearch.trim() ? null : this.listFocusYmd;
      return groupListEventsByDate(this.listViewEvents, focus);
    },
    listViewEventsGrouped() {
      const grouped = {};
      this.listViewGroups.forEach((group) => {
        grouped[group.dateStr] = group.events;
      });
      return grouped;
    },
    gridModeLabel() {
      if (this.gridDayFilter === null) return '7d';
      if (this.gridDayFilter === 'weekdays') return '5d';
      return '1d';
    },
    gridTimeSlots() {
      const slots = [];
      // 7:00 AM to 9:00 PM in 30-minute increments
      for (let hour = 7; hour < 21; hour++) {
        for (let min = 0; min < 60; min += 30) {
          const time = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
          const displayHour = hour > 12 ? hour - 12 : hour;
          const ampm = hour >= 12 ? 'pm' : 'am';
          const display = `${displayHour}:${String(min).padStart(2, '0')}${ampm}`;
          slots.push({ time, display });
        }
      }
      return slots;
    },
    gridWeekRangeDisplay() {
      if (!this.gridWeekStart) return '';
      const start = new Date(this.gridWeekStart);
      const end = new Date(this.gridWeekStart);
      end.setDate(end.getDate() + 6);

      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const startStr = `${dayNames[start.getDay()]} ${start.getMonth() + 1}/${start.getDate()}`;
      const endStr = `${dayNames[end.getDay()]} ${end.getMonth() + 1}/${end.getDate()}`;
      return `${startStr} to ${endStr}`;
    },
  },
  watch: {
    async showModal(newVal) {
      if (newVal) {
        // Check if personal calendar mode is requested via props
        if (this.personalCalendar) {
          this.showSharedCalendar = false;
          console.log('📅 [CALENDAR] Switched to personal calendar via prop');
        }

        // Set initial date if month/year props are provided
        if (this.initialMonth && this.initialYear) {
          this.currentDate = new Date(
            this.initialYear,
            this.initialMonth - 1,
            1,
          );
          console.log('📅 [CALENDAR] Set initial date to:', this.currentDate);

          // If day is also provided, select that specific date
          if (this.initialDay) {
            console.log('📅 [CALENDAR] Creating date with:', {
              year: this.initialYear,
              month: this.initialMonth,
              monthMinusOne: this.initialMonth - 1,
              day: this.initialDay,
            });
            const specificDate = new Date(
              this.initialYear,
              this.initialMonth - 1,
              this.initialDay,
            );
            this.selectedDate = specificDate;
            console.log('📅 [CALENDAR] Selected specific date:', specificDate);
            console.log(
              '📅 [CALENDAR] Date string:',
              specificDate.toDateString(),
            );
            console.log('📅 [CALENDAR] Date ISO:', specificDate.toISOString());
          }
        }

        // Load events first, then open panel if specific day selected
        await this.loadEvents();
        this.restoreEventFormDraft();

        // After events are loaded, open the events panel if a specific day was selected
        if (this.initialDay && this.selectedDate && !this.showEventModal) {
          this.showEventsPanel = true;
          console.log('📅 [CALENDAR] Opening events panel for selected date');
        }

        // Auto-open grid view if initialGridTab is set
        if (this.initialGridTab && !this.showGridEventForm) {
          this.gridActiveTab = this.initialGridTab;
          await this.openGridView();
        }

        // Auto-open list view if it's the saved default
        if (
          this.calendarDefaultView === 'list' &&
          !this.initialGridTab &&
          !this.initialDay &&
          !this.showEventModal
        ) {
          this.showListView = true;
        }
      } else {
        // When closing, reset to current date for next opening
        if (this.initialMonth && this.initialYear) {
          this.currentDate = new Date();
          console.log('📅 [CALENDAR] Reset to current date on close');
        }
      }
    },
    // Watch for changes to initial month/year props
    initialMonth(newVal) {
      if (newVal && this.initialYear && this.showModal) {
        this.currentDate = new Date(this.initialYear, newVal - 1, 1);
        console.log(
          '📅 [CALENDAR] Updated date from month prop:',
          this.currentDate,
        );
      }
    },
    initialYear(newVal) {
      if (newVal && this.initialMonth && this.showModal) {
        this.currentDate = new Date(newVal, this.initialMonth - 1, 1);
        console.log(
          '📅 [CALENDAR] Updated date from year prop:',
          this.currentDate,
        );
      }
    },
    initialDay(newVal) {
      if (newVal && this.initialMonth && this.initialYear && this.showModal) {
        console.log('📅 [CALENDAR] initialDay watcher - Creating date with:', {
          year: this.initialYear,
          month: this.initialMonth,
          monthMinusOne: this.initialMonth - 1,
          day: newVal,
        });
        const specificDate = new Date(
          this.initialYear,
          this.initialMonth - 1,
          newVal,
        );
        this.selectedDate = specificDate;
        this.showEventsPanel = true;
        console.log(
          '📅 [CALENDAR] Updated selected date from day prop:',
          specificDate,
        );
        console.log('📅 [CALENDAR] Date string:', specificDate.toDateString());
        console.log('📅 [CALENDAR] Date ISO:', specificDate.toISOString());
      }
    },
    isCalendarVisible(visible) {
      if (visible) this.placeCalendarResyncHint();
      else this.showCalendarResyncHint = false;
    },
    eventForm: {
      deep: true,
      handler() {
        this.schedulePersistEventFormDraft();
      },
    },
    showEventModal() {
      this.schedulePersistEventFormDraft();
    },
    showGridEventForm() {
      this.schedulePersistEventFormDraft();
    },
    'eventForm.date'(value) {
      if (this.restoringEventFormDraft) return;
      if (!(this.showEventModal || this.showGridEventForm)) return;
      if (calendarYmd(value)) return;
      const fallback =
        calendarYmd(this.selectedDate) || calendarYmd(new Date());
      if (fallback && value !== fallback) {
        this.eventForm.date = fallback;
      }
    },
    'eventForm.startTime'(newStartTime) {
      // If start time is set and end time is earlier than start time,
      // automatically set end time to 1 hour after start time
      if (newStartTime && this.eventForm.endTime) {
        const [startHour, startMin] = newStartTime.split(':').map(Number);
        const [endHour, endMin] = this.eventForm.endTime.split(':').map(Number);

        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;

        if (endMinutes <= startMinutes) {
          // Set end time to 1 hour after start time
          const newEndMinutes = startMinutes + 60;
          const newEndHour = Math.floor(newEndMinutes / 60) % 24;
          const newEndMin = newEndMinutes % 60;

          this.eventForm.endTime = `${String(newEndHour).padStart(
            2,
            '0',
          )}:${String(newEndMin).padStart(2, '0')}`;
        }
      }
    },
  },
  methods: {
    eventFormDraftHasContent() {
      const form = this.eventForm || {};
      const defaultMeetingUrl =
        localStorage.getItem('calendar_default_meeting_url') || '';
      const meetingUrl = (form.meetingUrl || '').trim();
      return !!(
        this.editingEvent ||
        (form.title && form.title.trim()) ||
        (form.description && form.description.trim()) ||
        (form.location && form.location.trim()) ||
        (meetingUrl && meetingUrl !== defaultMeetingUrl.trim()) ||
        form.purpose ||
        form.isMultiDay
      );
    },
    schedulePersistEventFormDraft() {
      if (this.restoringEventFormDraft) return;
      if (this.eventFormDraftTimer) {
        clearTimeout(this.eventFormDraftTimer);
      }
      this.eventFormDraftTimer = setTimeout(() => {
        this.persistEventFormDraft();
      }, 250);
    },
    persistEventFormDraft() {
      if (this.eventFormDraftSuppressed) return;
      if (this.restoringEventFormDraft) return;
      if (!(this.showEventModal || this.showGridEventForm)) return;
      if (!this.eventFormDraftHasContent()) return;
      const date = calendarYmd(this.eventForm.date);
      if (!date) return;
      const endDate = calendarYmd(this.eventForm.endDate) || date;
      try {
        localStorage.setItem(
          CALENDAR_EVENT_DRAFT_KEY,
          JSON.stringify({
            eventForm: { ...this.eventForm, date, endDate },
            editingEventId: this.editingEvent?.id || null,
            showEventModal: this.showEventModal,
            showGridEventForm: this.showGridEventForm,
          }),
        );
      } catch (error) {
        console.error('Error saving calendar event draft:', error);
      }
    },
    clearEventFormDraft() {
      this.eventFormDraftSuppressed = true;
      if (this.eventFormDraftTimer) {
        clearTimeout(this.eventFormDraftTimer);
        this.eventFormDraftTimer = null;
      }
      try {
        localStorage.removeItem(CALENDAR_EVENT_DRAFT_KEY);
      } catch (_) {
        /* ignore */
      }
    },
    readEventFormDraft() {
      try {
        const raw = localStorage.getItem(CALENDAR_EVENT_DRAFT_KEY);
        if (!raw) return null;
        const draft = JSON.parse(raw);
        if (!draft || typeof draft !== 'object' || !draft.eventForm) {
          return null;
        }
        return draft;
      } catch (_) {
        return null;
      }
    },
    applyEventFormDraft(draft) {
      if (!draft?.eventForm) return;
      this.restoringEventFormDraft = true;
      this.eventForm = {
        title: '',
        description: '',
        location: '',
        meetingUrl: '',
        date: '',
        endDate: '',
        isMultiDay: false,
        startTime: '09:00',
        endTime: '10:00',
        color: '#007bff',
        personal: false,
        maskForRole: 3,
        purpose: null,
        ...draft.eventForm,
      };
      if (draft.editingEventId) {
        this.editingEvent = this.events.find(
          (event) => event.id === draft.editingEventId,
        ) || {
          id: draft.editingEventId,
        };
      } else {
        this.editingEvent = null;
      }
      if (draft.showGridEventForm) {
        this.showGridEventForm = true;
        this.showEventModal = false;
        this.showGridView = true;
        if (!this.gridWeekStart) {
          const anchor = localDateFromYmd(this.eventForm.date) || new Date();
          const sunday = new Date(
            anchor.getFullYear(),
            anchor.getMonth(),
            anchor.getDate(),
          );
          sunday.setDate(sunday.getDate() - sunday.getDay());
          this.gridWeekStart = sunday;
          this.gridDatePicker = calendarYmd(anchor);
        }
      } else {
        this.showEventModal = true;
        this.showEventsPanel = true;
      }
      const selected = localDateFromYmd(this.eventForm.date);
      if (selected) {
        this.selectedDate = selected;
        this.currentDate = new Date(
          selected.getFullYear(),
          selected.getMonth(),
          1,
        );
      }
      this.$nextTick(() => {
        this.restoringEventFormDraft = false;
        if (this.showGridView) this.loadGridAvailability();
      });
    },
    restoreEventFormDraft() {
      const draft = this.readEventFormDraft();
      if (!draft) return;
      const decision = resolveCalendarEventDraft(draft, this.events, {
        eventsLoaded: this.eventsLoadOk,
      });
      if (decision.action === 'discard') {
        this.clearEventFormDraft();
        return;
      }
      if (decision.action !== 'restore') return;
      this.eventFormDraftSuppressed = false;
      this.applyEventFormDraft(decision.draft);
    },
    getColorLabel(color) {
      // Map colors to their letters and titles
      const colorMap = {
        '#007bff': { letter: 'M', title: 'Work Orders' }, // Blue
        '#28a745': { letter: 'G', title: 'General' }, // Green
        '#dc3545': { letter: '!', title: 'Urgent' }, // Red
        '#fd7e14': { letter: 'O', title: 'Office' }, // Orange
        '#6f42c1': { letter: 'P', title: 'Personal Meeting' }, // Purple
        '#20c997': { letter: 'L', title: 'Landscaping' }, // Teal/Lime
        '#ffc107': { letter: 'A', title: 'Appliances' }, // Yellow
        '#e83e8c': { letter: 'U', title: 'Utilities' }, // Pink
      };
      return colorMap[color] || { letter: '', title: '' };
    },
    getPurposeFullLabel(purposeKey) {
      const state = this.purposeStates.find((s) => s.key === purposeKey);
      return state ? state.fullLabel : purposeKey;
    },
    getVisibilityText(maskForRole) {
      // Map maskForRole to visibility text
      if (maskForRole === null || maskForRole === undefined) {
        return 'Everyone';
      }
      const roleMap = {
        1: 'Admin', // Only admins
        2: 'Poweruser and above', // Powerusers and admins
        3: 'Everyone', // All users (user role and above)
      };
      return roleMap[maskForRole] || 'Everyone';
    },
    closeCalendar() {
      this.showEventsPanel = false;
      this.showEventModal = false;
      this.showGridEventForm = false;
      this.selectedDate = null;

      // If in mobile standalone mode, navigate back instead of just closing
      if (this.isMobileStandalone) {
        this.$router.push('/mhome');
      } else {
        this.$emit('close');
      }
    },
    closeEventsPanel() {
      this.clearEventFormDraft();
      this.showEventsPanel = false;
      this.showEventModal = false;
      this.editingEvent = null;
      this.selectedDate = null;
    },

    async fetchUserRoles() {
      try {
        const response = await axios.get('/api/user-roles');
        this.userRoles = response.data.data || response.data;
        console.log('User roles loaded:', this.userRoles);
      } catch (error) {
        console.error('Error fetching user roles:', error);
      }
    },

    async fetchUserInfo() {
      try {
        const response = await axios.get('/api/user-info');
        this.userRole = response.data.role;
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    },

    async toggleSharedCalendar() {
      this.showSharedCalendar = !this.showSharedCalendar;
      await this.loadEvents();

      // Show notification about the switch
      const mode = this.showSharedCalendar ? 'shared' : 'personal';
      this.$emit(
        'notification',
        `Switched to ${mode} calendar view`,
        'info',
        3000,
      );
    },
    formatMonthYear(date) {
      return date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    },
    previousMonth() {
      const newDate = new Date(this.currentDate);
      newDate.setMonth(newDate.getMonth() - 1);
      this.currentDate = newDate;
    },
    nextMonth() {
      const newDate = new Date(this.currentDate);
      newDate.setMonth(newDate.getMonth() + 1);
      this.currentDate = newDate;
    },
    selectDate(day) {
      const selected = localDateFromYmd(day.date);
      if (!selected) return;
      if (this.showEventModal || this.showGridEventForm) {
        this.clearEventFormDraft();
        this.showEventModal = false;
        this.showGridEventForm = false;
        this.editingEvent = null;
      }
      this.selectedDate = selected;
      this.showEventsPanel = true;

      if (day.otherMonth) {
        // Navigate to the appropriate month
        const dayDate = selected;
        const currentMonth = this.currentDate.getMonth();
        const dayMonth = dayDate.getMonth();

        if (
          dayMonth < currentMonth ||
          (dayMonth === 11 && currentMonth === 0)
        ) {
          // Previous month
          this.previousMonth();
        } else {
          // Next month
          this.nextMonth();
        }
      }
    },
    goToToday() {
      this.currentDate = new Date();
      this.selectedDate = new Date();
      this.showEventsPanel = true;
    },
    openGoogleMaps(location) {
      if (!location) return;

      // Encode the location for URL
      const encodedLocation = encodeURIComponent(location);

      // Open Google Maps in satellite view in a new window/tab
      const mapsUrl = `https://maps.google.com/maps?q=${encodedLocation}&t=k`;
      window.open(mapsUrl, '_blank');
    },
    openMeetingUrl(meetingUrl) {
      if (!meetingUrl) return;

      // Open the meeting URL in a new window/tab
      window.open(meetingUrl, '_blank');
    },
    copyCalendarLink(event) {
      // Format event details as text
      const formatTime = (timeStr) => {
        if (!timeStr) return '';
        const [hours, minutes] = timeStr.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${minutes} ${ampm}`;
      };

      // Parse and format date
      let eventDate, month, year, day;
      if (typeof event.event_date === 'string') {
        const dateParts = event.event_date.split('-');
        if (dateParts.length === 3) {
          year = parseInt(dateParts[0]);
          month = parseInt(dateParts[1]);
          day = parseInt(dateParts[2]);
          eventDate = new Date(year, month - 1, day);
        } else {
          eventDate = new Date(event.event_date);
          month = eventDate.getMonth() + 1;
          year = eventDate.getFullYear();
          day = eventDate.getDate();
        }
      } else {
        eventDate = new Date(event.event_date);
        month = eventDate.getMonth() + 1;
        year = eventDate.getFullYear();
        day = eventDate.getDate();
      }

      const dateStr = eventDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      // Build event details text
      let eventText = `📅 ${event.title}\n\n`;
      eventText += `Date: ${dateStr}\n`;

      if (event.start_time) {
        eventText += `Time: ${formatTime(event.start_time)}`;
        if (event.end_time) {
          eventText += ` - ${formatTime(event.end_time)}`;
        }
        eventText += '\n';
      }

      if (event.location) {
        eventText += `Location: ${event.location}\n`;
      }

      if (event.description) {
        // Strip HTML tags from description
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = event.description;
        const plainDesc = tempDiv.textContent || tempDiv.innerText || '';
        eventText += `\nDescription:\n${plainDesc}\n`;
      }

      const hostname = window.location.hostname;

      // Check if we're in development mode
      const isDev =
        process.env.NODE_ENV === 'development' ||
        hostname === 'localhost' ||
        hostname === 'pixiev2';

      // Always use production URL in the event details text for sharing
      const calendarUrl = `https://pixie.bigtapapp.com/calendar?month=${month}&year=${year}&day=${day}`;

      eventText += `\nLink: ${calendarUrl}`;

      console.log('copyCalendarLink - Environment check:', {
        NODE_ENV: process.env.NODE_ENV,
        hostname,
        isDev,
        calendarUrl,
      });

      if (isDev) {
        console.log('Creating popup with event text:', eventText);

        // Show popup with selectable text in DEV
        const popupHtml = `
          <div style="background: white; padding: 20px; border-radius: 8px; max-width: 500px; max-height: 70vh; overflow-y: auto; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
              <h3 style="margin: 0; color: #333;">Event Details</h3>
              <button class="close-popup-btn" style="background: none; border: none; font-size: 24px; cursor: pointer; color: #999;">&times;</button>
            </div>
            <textarea readonly class="event-details-textarea" style="width: 92%; min-height: 300px; padding: 15px; font-family: monospace; font-size: 14px; border: 1px solid #ddd; border-radius: 4px; resize: vertical; cursor: pointer;" title="Click to select all">${eventText}</textarea>
            <button class="copy-details-btn" style="margin-top: 10px; padding: 10px 20px; background: #4CAF50; color: white; border: none; border-radius: 4px; cursor: pointer; width: 100%;">Copy to Clipboard</button>
          </div>
        `;

        // Create a simple modal
        const overlay = document.createElement('div');
        overlay.className = 'swal2-container';
        overlay.style.cssText =
          'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 999999;';
        overlay.innerHTML = `<div class="swal2-popup">${popupHtml}</div>`;

        // Add event listeners after appending to DOM
        document.body.appendChild(overlay);

        // Textarea click to select
        const textarea = overlay.querySelector('.event-details-textarea');
        textarea.addEventListener('click', function () {
          this.select();
        });

        // Close button
        const closeBtn = overlay.querySelector('.close-popup-btn');
        closeBtn.addEventListener('click', () => {
          overlay.remove();
        });

        // Copy button
        const copyBtn = overlay.querySelector('.copy-details-btn');
        copyBtn.addEventListener('click', () => {
          // Use textarea selection and execCommand for non-HTTPS environments
          textarea.select();
          textarea.setSelectionRange(0, 99999); // For mobile devices

          try {
            const successful = document.execCommand('copy');
            if (successful) {
              overlay.remove();
            } else {
              alert('Failed to copy to clipboard');
            }
          } catch (err) {
            console.error('Copy failed:', err);
            alert('Failed to copy to clipboard');
          }
        });

        // Click outside to close
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            overlay.remove();
          }
        });

        console.log('Popup appended to body, overlay element:', overlay);
      } else {
        // Copy directly to clipboard in PRODUCTION
        navigator.clipboard
          .writeText(eventText)
          .then(() => {
            this.$emit(
              'notification',
              'Event details copied to clipboard',
              'success',
              3000,
            );
            console.log('Event details copied to clipboard:', eventText);
          })
          .catch((err) => {
            console.error('Failed to copy event details:', err);
            // Fallback method for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = eventText;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
              document.execCommand('copy');
              this.$emit(
                'notification',
                'Event details copied to clipboard',
                'success',
                3000,
              );
            } catch (fallbackErr) {
              console.error('Fallback copy failed:', fallbackErr);
              this.$emit(
                'notification',
                'Failed to copy to clipboard',
                'error',
                3000,
              );
            }
            document.body.removeChild(textArea);
          });
      }
    },
    addEventForDay(day) {
      const selected = localDateFromYmd(day.date);
      if (!selected) return;
      if (day.otherMonth) {
        // Navigate to the appropriate month and open events panel
        const dayDate = selected;
        const currentMonth = this.currentDate.getMonth();
        const dayMonth = dayDate.getMonth();

        if (
          dayMonth < currentMonth ||
          (dayMonth === 11 && currentMonth === 0)
        ) {
          // Previous month
          this.previousMonth();
        } else {
          // Next month
          this.nextMonth();
        }

        // After navigation, select the date and open events panel
        this.$nextTick(() => {
          this.selectedDate = selected;
          this.showEventsPanel = true;
          this.showEventModal = false;
        });
      } else {
        this.selectedDate = selected;
        this.addEvent();
      }
    },
    addEvent() {
      this.eventFormDraftSuppressed = false;
      this.editingEvent = null;

      // Get saved meeting URL from localStorage for auto-population
      const savedMeetingUrl =
        localStorage.getItem('calendar_default_meeting_url') || '';

      const today = calendarYmd(this.selectedDate) || calendarYmd(new Date());

      this.eventForm = {
        title: '',
        description: '',
        location: '',
        meetingUrl: savedMeetingUrl,
        date: today,
        endDate: today,
        isMultiDay: false,
        startTime: '09:00',
        endTime: '10:00',
        color: '#007bff',
        personal: false,
      };
      this.showEventModal = true;
    },
    editEvent(event) {
      this.eventFormDraftSuppressed = false;
      this.editingEvent = event;

      // Keep the YYYY-MM-DD prefix. Parsing the API timestamp and then
      // formatting it can land on 12/30/69 when the value is empty.
      const formattedDate =
        calendarYmd(event.event_date) || calendarYmd(new Date());
      const formattedEndDate = calendarYmd(event.end_date);
      const isMultiDay = !!(
        formattedEndDate && formattedEndDate !== formattedDate
      );

      this.eventForm = {
        title: event.title,
        description: event.description || '',
        location: event.location || '',
        meetingUrl: event.meeting_url || '',
        date: formattedDate,
        endDate: formattedEndDate || formattedDate,
        isMultiDay,
        startTime: event.start_time,
        endTime: event.end_time,
        color: event.color,
        personal: event.personal === 1 || event.personal === true,
        maskForRole: event.maskForRole || null,
        purpose: event.purpose || null,
      };
      console.log('Loading event into form:', {
        eventId: event.id,
        title: event.title,
        personalFromDB: event.personal,
        personalInForm: this.eventForm.personal,
      });
      this.showEventModal = true;
    },
    closeEventModal() {
      this.clearEventFormDraft();
      this.showEventModal = false;
      this.editingEvent = null;
      // Cancel used to leave the day unset, so the list stayed empty
      // (or on 12/30/69). Show a real day instead.
      if (!calendarYmd(this.selectedDate)) {
        const today = new Date();
        this.selectedDate = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
        );
        this.currentDate = new Date(today.getFullYear(), today.getMonth(), 1);
        this.showEventsPanel = true;
      }
    },
    navigateEventDate(offset) {
      const d = shiftLocalDay(this.selectedDate, offset);
      this.selectedDate = d;
      this.currentDate = new Date(d.getFullYear(), d.getMonth(), 1);
      // Keep the event form date in sync if the form is open
      if (this.showEventModal || this.showGridEventForm) {
        this.eventForm.date = calendarYmd(d);
      }
    },
    async saveEvent() {
      if (this.savingEvent) return;
      this.persistEventFormDraft();
      try {
        console.log(
          'CalendarTool: Saving event using baseURL:',
          axios.defaults.baseURL,
        );

        // Save meeting URL to localStorage for future use (only if it's not empty)
        if (this.eventForm.meetingUrl && this.eventForm.meetingUrl.trim()) {
          localStorage.setItem(
            'calendar_default_meeting_url',
            this.eventForm.meetingUrl.trim(),
          );
        }

        // Ensure required fields are present. Refuse an epoch date so Update
        // cannot store 12/30/69 from an empty iPhone date field.
        const eventDate = calendarYmd(this.eventForm.date);
        if (!this.eventForm.title || !eventDate) {
          this.$emit(
            'notification',
            'Please fill in all required fields (Title and Date)',
            'error',
          );
          return;
        }

        if (!this.eventForm.isMultiDay) {
          // Ensure start and end times are present
          if (!this.eventForm.startTime || !this.eventForm.endTime) {
            this.$emit(
              'notification',
              'Please fill in start and end times',
              'error',
            );
            return;
          }

          // Validate that end time is after start time
          const [startHour, startMin] = this.eventForm.startTime
            .split(':')
            .map(Number);
          const [endHour, endMin] = this.eventForm.endTime
            .split(':')
            .map(Number);

          const startMinutes = startHour * 60 + startMin;
          const endMinutes = endHour * 60 + endMin;

          if (endMinutes <= startMinutes) {
            this.$emit(
              'notification',
              'End time must be after start time',
              'error',
            );
            return;
          }
        } else {
          // Validate that end date >= start date
          if (
            this.eventForm.endDate &&
            this.eventForm.endDate < this.eventForm.date
          ) {
            this.$emit(
              'notification',
              'End date must be on or after start date',
              'error',
            );
            return;
          }
        }

        const eventData = {
          title: this.eventForm.title,
          description: this.eventForm.description || '',
          location: this.eventForm.location || '',
          meeting_url: this.eventForm.meetingUrl || '',
          event_date: eventDate,
          end_date: this.eventForm.isMultiDay
            ? calendarYmd(this.eventForm.endDate) || null
            : null,
          start_time: this.eventForm.isMultiDay
            ? '00:00'
            : this.eventForm.startTime,
          end_time: this.eventForm.isMultiDay
            ? '23:59'
            : this.eventForm.endTime,
          color: this.eventForm.color || '#007bff',
          personal: this.eventForm.personal ? 1 : 0,
          maskForRole: this.eventForm.maskForRole,
          purpose: this.eventForm.purpose || null,
        };

        console.log('Saving event with data:', eventData);

        this.savingEvent = true;
        if (this.editingEvent) {
          await axios.put(
            `/api/calendar/events/${this.editingEvent.id}`,
            eventData,
          );
          this.$emit('notification', 'Event updated successfully', 'success');
        } else {
          await axios.post('/api/calendar/events', eventData);
          this.$emit('notification', 'Event created successfully', 'success');
        }

        await this.loadEvents();
        this.clearEventFormDraft();
        this.closeEventModal();
      } catch (error) {
        console.error('Error saving event:', error);
        this.persistEventFormDraft();
        this.$emit(
          'notification',
          'Error saving event. Please try again.',
          'error',
        );
      } finally {
        this.savingEvent = false;
      }
    },
    async deleteEvent() {
      // Open confirmation modal instead of using confirm()
      this.showDeleteConfirmModal = true;
    },
    copyEvent() {
      // Create a copy of the current event with start time = current end time
      // and description cleared
      const currentEvent = this.editingEvent;

      // Calculate new start time from current event's end time
      const newStartTime = currentEvent.end_time;

      // Calculate new end time (1 hour after new start time)
      const [hours, minutes] = newStartTime.split(':').map(Number);
      const newEndMinutes = hours * 60 + minutes + 60;
      const newEndHour = Math.floor(newEndMinutes / 60) % 24;
      const newEndMin = newEndMinutes % 60;
      const newEndTime = `${String(newEndHour).padStart(2, '0')}:${String(
        newEndMin,
      ).padStart(2, '0')}`;

      // Set up form for new event (not editing anymore)
      this.editingEvent = null;

      this.eventForm = {
        title: currentEvent.title,
        description: '', // Clear description
        location: currentEvent.location || '',
        meetingUrl: currentEvent.meeting_url || '',
        date: this.eventForm.date, // Keep same date
        startTime: newStartTime,
        endTime: newEndTime,
        color: currentEvent.color,
        personal: currentEvent.personal === 1 || currentEvent.personal === true,
        maskForRole: currentEvent.maskForRole || null,
      };

      console.log('Created copy of event with new times:', {
        originalEnd: currentEvent.end_time,
        newStart: newStartTime,
        newEnd: newEndTime,
      });
    },
    closeDeleteConfirmModal() {
      this.showDeleteConfirmModal = false;
    },
    async confirmDelete() {
      try {
        await axios.delete(`/api/calendar/events/${this.editingEvent.id}`);
        await this.loadEvents();
        this.clearEventFormDraft();
        this.closeEventModal();
        this.closeDeleteConfirmModal();
        this.$emit('notification', 'Event deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting event:', error);
        this.$emit(
          'notification',
          'Error deleting event. Please try again.',
          'error',
        );
        this.closeDeleteConfirmModal();
      }
    },
    startRefreshHold(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.cancelRefreshHold();
      this.refreshHoldFired = false;
      const el = event.currentTarget;
      if (el && el.setPointerCapture && event.pointerId != null) {
        try {
          el.setPointerCapture(event.pointerId);
        } catch {
          /* ignore */
        }
      }
      this.refreshHoldTimer = setTimeout(() => {
        this.refreshHoldFired = true;
        this.forceRefreshCalendar();
      }, CALENDAR_REFRESH_HOLD_MS);
    },
    cancelRefreshHold() {
      if (this.refreshHoldTimer) {
        clearTimeout(this.refreshHoldTimer);
        this.refreshHoldTimer = null;
      }
    },
    onRefreshClick() {
      this.cancelRefreshHold();
      if (this.refreshHoldFired) {
        this.refreshHoldFired = false;
        return;
      }
      this.refreshCalendar();
    },
    async refreshCalendar() {
      if (!calendarYmd(this.selectedDate)) {
        const today = new Date();
        this.selectedDate = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
        );
        this.currentDate = new Date(today.getFullYear(), today.getMonth(), 1);
      }
      this.refreshingCalendar = true;
      try {
        await this.loadEvents();
        if (this.showGridView) {
          await this.loadGridAvailability();
        }
        this.$emit('notification', 'Calendar refreshed', 'success');
      } finally {
        this.refreshingCalendar = false;
      }
    },
    async forceRefreshCalendar() {
      const formWasOpen = this.showEventModal || this.showGridEventForm;
      const dateBad = !calendarYmd(this.selectedDate);
      this.clearEventFormDraft();
      this.showEventModal = false;
      this.showGridEventForm = false;
      this.showDeleteConfirmModal = false;
      this.editingEvent = null;
      if (dateBad) {
        const today = new Date();
        this.selectedDate = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
        );
        this.currentDate = new Date(today.getFullYear(), today.getMonth(), 1);
      }
      if ((formWasOpen || dateBad) && !this.showGridView) {
        this.showEventsPanel = true;
      }
      this.refreshingCalendar = true;
      try {
        await this.loadEvents({ fresh: true });
        if (this.showGridView) {
          await this.loadGridAvailability();
        }
        this.$emit(
          'notification',
          'Cleared the saved edit and reloaded the calendar',
          'success',
        );
      } finally {
        this.refreshingCalendar = false;
      }
    },
    hideCalendarResyncHint() {
      this.showCalendarResyncHint = false;
    },
    hideCalendarResyncHintForever() {
      this.showCalendarResyncHint = false;
      markCalendarResyncHintSeen(localStorage);
    },
    placeCalendarResyncHint() {
      if (calendarResyncHintSeen(localStorage)) {
        this.showCalendarResyncHint = false;
        return;
      }
      this.$nextTick(() => {
        const button = this.$refs.calendarResyncBtn;
        const popup =
          button && button.closest && button.closest('.calendar-popup');
        if (
          !popup ||
          !button ||
          typeof popup.getBoundingClientRect !== 'function'
        ) {
          return;
        }
        const popupBox = popup.getBoundingClientRect();
        const buttonBox = button.getBoundingClientRect();
        const header = popup.querySelector('.calendar-header');
        const headerBox =
          header && typeof header.getBoundingClientRect === 'function'
            ? header.getBoundingClientRect()
            : buttonBox;
        // The header paints above the hint. Its padding hangs below the
        // button and covers the arrow point, so the callout starts under
        // that bar. The point sits 2px down in the 64px arrow.
        const tipInset = 2;
        const gapBelowHeader = 6;
        const clearOf = Math.max(buttonBox.bottom, headerBox.bottom);
        this.calendarResyncHintTop = `${Math.round(
          clearOf - popupBox.top + gapBelowHeader - tipInset,
        )}px`;
        const centerFromRight =
          popupBox.right - (buttonBox.left + buttonBox.width / 2);
        this.calendarResyncArrowRight = `${Math.round(centerFromRight - 20)}px`;
        this.showCalendarResyncHint = true;
      });
    },
    async clearCacheAndResync() {
      this.refreshingCalendar = true;
      try {
        await clearCalendarEventCache();
        await this.loadEvents({ fresh: true });
        if (this.showGridView) {
          await this.loadGridAvailability();
        }
        this.$emit('notification', 'Calendar cache cleared and reloaded', 'success');
      } finally {
        this.refreshingCalendar = false;
      }
    },
    async loadEvents(options = {}) {
      try {
        // Add shared parameter - send 'false' only when personal calendar is requested
        const params = this.showSharedCalendar ? {} : { shared: 'false' };
        const headers = {};
        if (options.fresh) {
          params._ = Date.now();
          headers['Cache-Control'] = 'no-cache';
          headers.Pragma = 'no-cache';
        }
        const response = await axios.get('/api/calendar/events', {
          params,
          headers,
        });

        this.events = response.data.data || [];
        this.eventsLoadOk = true;
      } catch (error) {
        console.error('Error loading events:', error);
        this.events = [];
        this.eventsLoadOk = false;
        this.$emit('notification', 'Error loading calendar events', 'error');
      }
    },
    isEventOnDate(event, dateStr) {
      return eventOccursOn(event, dateStr);
    },
    getEventCount(dateStr) {
      const date = calendarYmd(dateStr);
      if (!date) return 0;
      return this.filteredEvents.filter((event) =>
        this.isEventOnDate(event, date),
      ).length;
    },
    getEventColor(dateStr) {
      const date = calendarYmd(dateStr);
      const eventsForDate = this.filteredEvents.filter((event) =>
        this.isEventOnDate(event, date),
      );
      // Return the color of the first event for the date, or default color
      return eventsForDate.length > 0 ? eventsForDate[0].color : '#dc3545';
    },
    hasPersonalEvents(dateStr) {
      const date = calendarYmd(dateStr);
      const eventsForDate = this.filteredEvents.filter((event) =>
        this.isEventOnDate(event, date),
      );
      return eventsForDate.some(
        (event) => event.personal === 1 || event.personal === true,
      );
    },
    formatSelectedDate() {
      return formatCalendarDayLabel(this.selectedDate);
    },
    renderMarkDown(text) {
      if (!text) return '';

      try {
        // Configure marked to support tables and other GitHub Flavored Markdown features
        marked.setOptions({
          gfm: true, // Enable GitHub Flavored Markdown
          tables: true, // Enable table syntax
          breaks: false, // Don't convert \n to <br>
          pedantic: false, // Don't be as strict
          sanitize: false, // Allow HTML
          smartLists: true, // Use smarter list behavior
          smartypants: false, // Don't use smart quotes
        });

        let processedText = text;

        // Process phone numbers before markdown parsing
        processedText = this.linkifyPhoneNumbers(processedText);

        // Replace [DONE] with checkmark before markdown parsing
        processedText = processedText.replace(
          /\[DONE\]/gi,
          '<span class="done-checkmark">✓</span>',
        );

        // Parse markdown
        const markdown = marked.parse(processedText);

        return markdown;
      } catch (error) {
        console.error('Error parsing markdown:', error);
        return text; // Fallback to displaying the raw text if parsing fails
      }
    },
    linkifyPhoneNumbers(text) {
      if (!text) return '';

      // Pattern to match various phone number formats:
      // (123) 456-7890, 123-456-7890, 123.456.7890, 1234567890
      // With optional country code: +1 or 1
      const phonePattern = /(\+?1?\s*)?(\(?\d{3}\)?[\s.-]?)?\d{3}[\s.-]?\d{4}/g;

      return text.replace(phonePattern, (match) => {
        // Clean the phone number for the tel: link (remove all non-digits except leading +)
        const cleanNumber = match.replace(/[^\d+]/g, '');

        // Only create a tel link if we have a valid length (10 or 11 digits)
        if (cleanNumber.length >= 10 && cleanNumber.length <= 11) {
          return `<a href="tel:${cleanNumber}" class="phone-link"><i class="fas fa-phone"></i> ${match}</a>`;
        }

        return match;
      });
    },
    formatTime(timeStr) {
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'pm' : 'am';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes}${ampm}`;
    },
    // Touch gesture handlers for swipe navigation
    handleTouchStart(e) {
      // Only handle swipes on mobile
      if (!this.isMobile) return;

      // Don't handle swipes if events panel or form is open
      if (this.showEventsPanel || this.showEventModal) return;

      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
    },
    handleTouchEnd(e) {
      // Only handle swipes on mobile
      if (!this.isMobile) return;

      // Don't handle swipes if events panel or form is open
      if (this.showEventsPanel || this.showEventModal) return;

      this.touchEndX = e.changedTouches[0].clientX;
      this.touchEndY = e.changedTouches[0].clientY;

      this.handleSwipe();
    },
    handleSwipe() {
      const swipeThreshold = 50; // Minimum distance for a swipe
      const verticalThreshold = 30; // Maximum vertical movement to still count as horizontal swipe

      const horizontalDistance = this.touchEndX - this.touchStartX;
      const verticalDistance = Math.abs(this.touchEndY - this.touchStartY);

      // Make sure it's primarily a horizontal swipe
      if (verticalDistance > verticalThreshold) return;

      // Swipe left (next month)
      if (horizontalDistance < -swipeThreshold) {
        this.nextMonth();
        console.log('📱 [CALENDAR] Swiped left - next month');
      }
      // Swipe right (previous month)
      else if (horizontalDistance > swipeThreshold) {
        this.previousMonth();
        console.log('📱 [CALENDAR] Swiped right - previous month');
      }
    },
    // ===== Scheduling Grid Methods =====
    formatDateForKey(date) {
      return calendarYmd(date) || '';
    },
    getSlotKey(date, time) {
      return `${date}_${time}`;
    },
    async openGridView() {
      // Initialize gridWeekStart to the Sunday of the current week
      const today = new Date();
      const dayOfWeek = today.getDay();
      const sunday = new Date(today);
      sunday.setDate(today.getDate() - dayOfWeek);
      sunday.setHours(0, 0, 0, 0);
      this.gridWeekStart = sunday;
      this.gridDatePicker = this.formatDateForKey(today);
      this.gridDayFilter = 'weekdays';
      this.showGridView = true;
      await this.loadGridAvailability();
    },
    closeGridView() {
      if (this.showGridEventForm) this.clearEventFormDraft();
      this.showGridView = false;
      this.showGridEventForm = false;
      this.editingEvent = null;
      this.gridDayFilter = null;
    },
    openListView() {
      this.showListView = true;
      // First time clicking list, ask if they want it as default
      if (!this.hasAskedDefaultView) {
        this.showDefaultViewPopup = true;
      }
    },
    closeListView() {
      this.showListView = false;
      this.listFocusYmd = null;
    },
    scrollListToFocus() {
      const ymd = this.listFocusYmd;
      const root = this.$refs.listContainer;
      if (!ymd || !root || typeof root.querySelector !== 'function') return;
      const header = root.querySelector(`[data-list-date="${ymd}"]`);
      if (!header) return;
      const rootTop = root.getBoundingClientRect().top;
      const headerTop = header.getBoundingClientRect().top;
      root.scrollTop += headerTop - rootTop;
    },
    printEscapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
    buildCalendarEventUrl(event) {
      let month;
      let year;
      let day;
      if (typeof event.event_date === 'string') {
        const dateParts = event.event_date.split('-');
        if (dateParts.length === 3) {
          year = parseInt(dateParts[0], 10);
          month = parseInt(dateParts[1], 10);
          day = parseInt(dateParts[2], 10);
        } else {
          const eventDate = new Date(event.event_date);
          month = eventDate.getMonth() + 1;
          year = eventDate.getFullYear();
          day = eventDate.getDate();
        }
      } else {
        const eventDate = new Date(event.event_date);
        month = eventDate.getMonth() + 1;
        year = eventDate.getFullYear();
        day = eventDate.getDate();
      }
      return `https://pixie.bigtapapp.com/calendar?month=${month}&year=${year}&day=${day}&event=${event.id}`;
    },
    async buildCalendarEventQrDataUrl(event) {
      const url = this.buildCalendarEventUrl(event);
      try {
        return await QRCode.toDataURL(url, {
          width: 120,
          margin: 1,
        });
      } catch (err) {
        console.error('Failed to generate QR code:', err);
        return '';
      }
    },
    async buildCalendarPrintQrMap() {
      const qrByEventId = {};
      await Promise.all(
        this.listViewEvents.map(async (event) => {
          qrByEventId[event.id] = await this.buildCalendarEventQrDataUrl(event);
        }),
      );
      return qrByEventId;
    },
    formatCalendarPrintDescription(event) {
      const raw = event.description;
      if (!raw || !String(raw).trim()) {
        return '<p class="cal-print-empty">No description</p>';
      }
      return `<div class="cal-print-body">${this.renderMarkDown(String(raw))}</div>`;
    },
    buildCalendarPrintEventCard(event, options = {}) {
      const title = this.printEscapeHtml(event.title || 'Untitled event');
      const timeLine =
        event.start_time || event.end_time
          ? `<div class="cal-print-meta-line">Time: ${this.printEscapeHtml(
              [
                event.start_time ? this.formatTime(event.start_time) : '',
                event.end_time ? this.formatTime(event.end_time) : '',
              ]
                .filter(Boolean)
                .join(' - '),
            )}</div>`
          : '';

      const purposeLine = event.purpose
        ? `<div class="cal-print-meta-line">Purpose: ${this.printEscapeHtml(
            this.getPurposeFullLabel(event.purpose),
          )}</div>`
        : '';

      const locationLine = event.location
        ? `<div class="cal-print-meta-line">Location: ${this.printEscapeHtml(
            event.location,
          )}</div>`
        : '';

      const visibilityLine = `<div class="cal-print-meta-line">Visibility: ${this.printEscapeHtml(
        this.getVisibilityText(event.maskForRole),
      )}</div>`;

      const colorLabel = this.getColorLabel(event.color);
      const categoryLine = colorLabel.title
        ? `<div class="cal-print-meta-line">Category: ${this.printEscapeHtml(
            colorLabel.title,
          )}</div>`
        : '';

      const qrDataUrl = options.qrDataUrl || '';
      const qrHtml = qrDataUrl
        ? `<div class="cal-print-entry-qr"><img src="${qrDataUrl}" alt="Event: ${title}" /></div>`
        : '<div class="cal-print-entry-qr"></div>';

      return `
        <article class="cal-print-entry">
          <div class="cal-print-entry-row">
            <div class="cal-print-entry-main">
              <div class="cal-print-entry-header">
                <div class="cal-print-title">${title}</div>
                ${timeLine}
                ${purposeLine}
                ${locationLine}
                ${categoryLine}
                ${visibilityLine}
              </div>
              ${this.formatCalendarPrintDescription(event)}
            </div>
            ${qrHtml}
          </div>
        </article>
      `;
    },
    buildCalendarPrintByDate(qrByEventId) {
      const grouped = this.listViewEventsGrouped;
      return Object.entries(grouped)
        .filter(([, events]) => events.length)
        .map(([dateStr, events]) => {
          const header = this.printEscapeHtml(
            this.formatListDateHeader(dateStr, events),
          );
          const parts = [];
          events.forEach((event, idx) => {
            parts.push(
              this.buildCalendarPrintEventCard(event, {
                qrDataUrl: qrByEventId[event.id] || '',
              }),
            );
            if (idx < events.length - 1) {
              parts.push('<hr class="cal-print-separator" />');
            }
          });
          return `
            <section class="cal-print-section">
              <h2>${header} <span class="cal-print-count">(${events.length})</span></h2>
              ${parts.join('')}
            </section>
          `;
        })
        .join('');
    },
    async printListView() {
      if (!this.listViewEvents.length) {
        window.alert('No events to print.');
        return;
      }

      const qrByEventId = await this.buildCalendarPrintQrMap();
      const generatedAt = new Date().toLocaleString();
      const filterLine = this.listViewSearch.trim()
        ? `<div class="cal-print-meta">Filter: ${this.printEscapeHtml(
            this.listViewSearch.trim(),
          )}</div>`
        : '';
      const bodyHtml = this.buildCalendarPrintByDate(qrByEventId);

      const printHtml = `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>Upcoming Calendar Events</title>
            <style>
              @page { size: Letter portrait; margin: 0.55in; }
              body {
                font-family: Arial, sans-serif;
                color: #222;
                margin: 0;
                font-size: 12px;
                line-height: 1.4;
              }
              h1 {
                margin: 0 0 4px;
                font-size: 18px;
              }
              h2 {
                margin: 18px 0 8px;
                font-size: 14px;
                color: #4a3d52;
                border-bottom: 1px solid #ccc;
                padding-bottom: 4px;
              }
              .cal-print-meta {
                margin-bottom: 10px;
                color: #555;
                font-size: 11px;
              }
              .cal-print-count {
                font-weight: 400;
                color: #666;
                font-size: 12px;
              }
              .cal-print-section:first-of-type h2 {
                margin-top: 8px;
              }
              .cal-print-entry {
                margin: 0 0 8px;
                page-break-inside: avoid;
              }
              .cal-print-separator {
                border: none;
                border-top: 1px solid #ccc;
                margin: 10px 0;
              }
              .cal-print-entry-row {
                display: flex;
                align-items: flex-start;
                gap: 8px;
              }
              .cal-print-entry-main {
                flex: 0 0 85%;
                width: 85%;
                max-width: 85%;
              }
              .cal-print-entry-qr {
                flex: 0 0 15%;
                width: 15%;
                max-width: 15%;
                text-align: center;
              }
              .cal-print-entry-qr img {
                width: 100%;
                max-width: 100%;
                height: auto;
              }
              .cal-print-title {
                font-weight: 700;
                font-size: 12px;
                margin-bottom: 2px;
              }
              .cal-print-meta-line {
                font-size: 11px;
                color: #555;
                margin-bottom: 2px;
              }
              .cal-print-body {
                margin-top: 6px;
                white-space: normal;
                word-wrap: break-word;
                overflow: visible;
                font-size: 12px;
                color: #333;
              }
              .cal-print-body p {
                margin: 4px 0;
              }
              .cal-print-body ul,
              .cal-print-body ol {
                margin: 4px 0;
                padding-left: 18px;
              }
              .cal-print-empty {
                color: #777;
                font-style: italic;
                margin: 4px 0 0;
              }
            </style>
          </head>
          <body>
            <h1>Upcoming Calendar Events</h1>
            <div class="cal-print-meta">Generated: ${this.printEscapeHtml(generatedAt)} · ${this.listViewEvents.length} events · ${this.printEscapeHtml(this.listHeaderRange)}</div>
            ${filterLine}
            ${bodyHtml}
          </body>
        </html>
      `;

      const printWindow = window.open('', '_blank', 'width=960,height=720');
      if (!printWindow) {
        window.alert('Please allow pop-ups to print the list.');
        return;
      }
      printWindow.document.open();
      printWindow.document.write(printHtml);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 200);
    },
    setDefaultView(view) {
      this.calendarDefaultView = view;
      localStorage.setItem('calendarDefaultView', view);
      this.hasAskedDefaultView = true;
      localStorage.setItem('calendarDefaultViewAsked', 'true');
      this.showDefaultViewPopup = false;
      this.showViewSettingsPopup = false;
    },
    dismissDefaultViewPopup() {
      this.hasAskedDefaultView = true;
      localStorage.setItem('calendarDefaultViewAsked', 'true');
      this.showDefaultViewPopup = false;
    },
    formatListDateHeader(dateStr, events = []) {
      const ymd = calendarYmd(dateStr);
      const date = localDateFromYmd(ymd);
      if (!date) return '';
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const eventDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
      );

      const diffTime = eventDate.getTime() - today.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
      const awayLabel =
        diffDays < 0
          ? `${Math.abs(diffDays)}d ago`
          : `${diffDays}d away`;

      const dayLabel = date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      });

      const multiDayEvent = events.find((event) => {
        const endYmd = calendarYmd(event && event.end_date);
        return !!endYmd && endYmd > ymd;
      });

      if (multiDayEvent) {
        const endDate = localDateFromYmd(multiDayEvent.end_date);
        if (!endDate) return `${dayLabel} (${awayLabel})`;
        const startLabel = date.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
        });
        const endLabel = endDate.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
        });
        return `${startLabel} - ${endLabel} (${awayLabel})`;
      }

      if (eventDate.getTime() === today.getTime()) {
        return 'Today - ' + dayLabel;
      }
      if (eventDate.getTime() === tomorrow.getTime()) {
        return 'Tomorrow - ' + dayLabel;
      }
      return `${dayLabel} (${awayLabel})`;
    },
    toggleListDescription(eventId) {
      this.expandedListEvents = {
        ...this.expandedListEvents,
        [eventId]: !this.expandedListEvents[eventId],
      };
    },
    isDescriptionLong(description) {
      if (!description) return false;
      const lines = description.split('\n');
      return lines.length > 2 || description.length > 120;
    },
    editEventFromList(event) {
      const selected = localDateFromYmd(event.event_date);
      if (!selected) return;
      this.closeListView();
      // Navigate to the event's date and open events panel (not in edit mode)
      this.currentDate = new Date(
        selected.getFullYear(),
        selected.getMonth(),
        1,
      );
      this.selectedDate = selected;
      this.showEventsPanel = true;
    },
    addEventFromList() {
      this.closeListView();
      this.selectedDate = new Date();
      this.currentDate = new Date();
      this.showEventsPanel = true;
      this.$nextTick(() => {
        this.addEvent();
      });
    },
    cycleGridMode() {
      // Cycle: 7-day -> 5-day -> 1-day (today) -> 7-day
      if (this.gridDayFilter === null) {
        this.gridDayFilter = 'weekdays';
      } else if (this.gridDayFilter === 'weekdays') {
        // Single day: default to today's day-of-week
        this.gridDayFilter = new Date().getDay();
      } else {
        this.gridDayFilter = null;
      }
    },
    selectGridDay(day) {
      // Click a column header to jump straight to 1-day view for that day
      const idx = day.dayIndex;
      if (this.gridDayFilter === idx) {
        // Already showing this day — cycle back to 7-day
        this.gridDayFilter = null;
      } else {
        this.gridDayFilter = idx;
      }
    },
    gridSlotDblClick(date, time) {
      // Check if there's an event on this slot - if so, edit the first one
      const key = this.getSlotKey(date, time);
      const ev = this.gridEvents[key];

      if (ev && ev.isEvent && ev.events.length > 0) {
        // Find the matching calendar event to edit
        const gridEvent = ev.events[0];
        const matchingEvent = this.events.find(
          (e) => e.title === gridEvent.title,
        );
        if (matchingEvent) {
          this.eventFormDraftSuppressed = false;
          this.editEvent(matchingEvent);
          this.showGridEventForm = true;
          this.showEventModal = false; // Don't show the calendar's flyout
          return;
        }
      }

      // No event on this slot — open new event form pre-filled
      this.eventFormDraftSuppressed = false;
      this.editingEvent = null;
      const savedMeetingUrl =
        localStorage.getItem('calendar_default_meeting_url') || '';

      // Calculate end time (30 min after start)
      const [h, m] = time.split(':').map(Number);
      let endH = h;
      let endM = m + 30;
      if (endM >= 60) {
        endH++;
        endM -= 60;
      }
      const endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;

      this.eventForm = {
        title: '',
        description: '',
        location: '',
        meetingUrl: savedMeetingUrl,
        date: date,
        startTime: time,
        endTime: endTime,
        color: '#007bff',
        personal: false,
        maskForRole: null,
      };
      this.showGridEventForm = true;
      this.showEventModal = false;
    },
    closeGridEventForm() {
      this.clearEventFormDraft();
      this.showGridEventForm = false;
      this.editingEvent = null;
    },
    async saveGridEvent() {
      if (this.savingEvent) return;
      const eventDate = calendarYmd(this.eventForm.date);
      if (!this.eventForm.title || !eventDate) {
        this.$emit(
          'notification',
          'Please fill in all required fields (Title and Date)',
          'error',
        );
        return;
      }
      this.persistEventFormDraft();
      try {
        this.savingEvent = true;
        if (this.eventForm.meetingUrl && this.eventForm.meetingUrl.trim()) {
          localStorage.setItem(
            'calendar_default_meeting_url',
            this.eventForm.meetingUrl.trim(),
          );
        }

        const eventData = {
          title: this.eventForm.title,
          description: this.eventForm.description,
          location: this.eventForm.location,
          meeting_url: this.eventForm.meetingUrl,
          event_date: eventDate,
          start_time: this.eventForm.startTime,
          end_time: this.eventForm.endTime,
          color: this.eventForm.color || '#007bff',
          personal: this.eventForm.personal ? 1 : 0,
          maskForRole: this.eventForm.maskForRole,
        };

        if (this.editingEvent) {
          await axios.put(
            `/api/calendar/events/${this.editingEvent.id}`,
            eventData,
          );
          this.$emit('notification', 'Event updated successfully', 'success');
        } else {
          await axios.post('/api/calendar/events', eventData);
          this.$emit('notification', 'Event created successfully', 'success');
        }

        await this.loadEvents();
        await this.loadGridAvailability();
        this.clearEventFormDraft();
        this.closeGridEventForm();
      } catch (error) {
        console.error('Error saving event:', error);
        this.persistEventFormDraft();
        this.$emit(
          'notification',
          'Error saving event. Please try again.',
          'error',
        );
      } finally {
        this.savingEvent = false;
      }
    },
    async deleteEventFromGrid() {
      if (!this.editingEvent) return;
      try {
        await axios.delete(`/api/calendar/events/${this.editingEvent.id}`);
        this.$emit('notification', 'Event deleted successfully', 'success');
        await this.loadEvents();
        await this.loadGridAvailability();
        this.clearEventFormDraft();
        this.closeGridEventForm();
      } catch (error) {
        console.error('Error deleting event:', error);
        this.$emit('notification', 'Error deleting event', 'error');
      }
    },
    previousGridWeek() {
      const newStart = new Date(this.gridWeekStart);
      newStart.setDate(newStart.getDate() - 7);
      this.gridWeekStart = newStart;
      this.gridDatePicker = this.formatDateForKey(newStart);
      this.loadGridAvailability();
    },
    nextGridWeek() {
      const newStart = new Date(this.gridWeekStart);
      newStart.setDate(newStart.getDate() + 7);
      this.gridWeekStart = newStart;
      this.gridDatePicker = this.formatDateForKey(newStart);
      this.loadGridAvailability();
    },
    onGridDatePickerChange() {
      if (!this.gridDatePicker) return;
      const selectedDate = localDateFromYmd(this.gridDatePicker);
      if (!selectedDate) return;
      const dayOfWeek = selectedDate.getDay();
      const sunday = new Date(selectedDate);
      sunday.setDate(selectedDate.getDate() - dayOfWeek);
      sunday.setHours(0, 0, 0, 0);
      this.gridWeekStart = sunday;
      this.loadGridAvailability();
    },
    async loadGridAvailability() {
      if (!this.gridWeekStart) return;

      const startDate = this.formatDateForKey(this.gridWeekStart);
      const endDate = new Date(this.gridWeekStart);
      endDate.setDate(endDate.getDate() + 6);
      const endDateStr = this.formatDateForKey(endDate);

      try {
        const response = await axios.get('/api/scheduling/availability', {
          params: { start_date: startDate, end_date: endDateStr },
        });

        // Build availability map (pure availability, no events)
        const availMap = {};
        if (response.data.data) {
          response.data.data.forEach((slot) => {
            const dateStr = calendarYmd(slot.slot_date);
            if (!dateStr) return;
            const timeStr = slot.slot_time.substring(0, 5); // "HH:mm"
            const key = this.getSlotKey(dateStr, timeStr);
            availMap[key] = {
              available: slot.available === 1,
              purpose:
                slot.purpose || (slot.available === 1 ? 'anything' : null),
              location: slot.location,
              reserved_by: slot.reserved_by,
            };
          });
        }

        // Build separate events map
        const eventsMap = {};
        this.events.forEach((event) => {
          const eventDate = calendarYmd(event.event_date);
          // Only process events within this grid week
          if (!eventDate || eventDate < startDate || eventDate > endDateStr) {
            return;
          }
          if (!event.start_time) return;

          // Parse start and end times to figure out which 30-min slots are covered
          const [startH, startM] = event.start_time.split(':').map(Number);
          let endH, endM;
          if (event.end_time) {
            [endH, endM] = event.end_time.split(':').map(Number);
          } else {
            // Default to 30 minutes if no end time
            endH = startH;
            endM = startM + 30;
            if (endM >= 60) {
              endH++;
              endM -= 60;
            }
          }

          // Snap start down and end up to 30-min grid boundaries
          const snapStart = startM < 30 ? 0 : 30;
          const snapEnd = endM > 30 ? 60 : endM > 0 ? 30 : 0;
          let curH = startH;
          let curM = snapStart;
          const limitH = snapEnd === 60 ? endH + 1 : endH;
          const limitM = snapEnd === 60 ? 0 : snapEnd;

          while (curH < limitH || (curH === limitH && curM < limitM)) {
            // Only fill slots within the grid range (07:00 - 20:30)
            if (curH >= 7 && (curH < 21 || (curH === 21 && curM === 0))) {
              const timeStr = `${String(curH).padStart(2, '0')}:${String(curM).padStart(2, '0')}`;
              const key = this.getSlotKey(eventDate, timeStr);
              const eventEntry = {
                title: event.title || 'Event',
                description: event.description || '',
                color: event.color || '#007bff',
              };
              // Accumulate events — multiple may overlap the same slot
              if (eventsMap[key] && eventsMap[key].isEvent) {
                eventsMap[key].events.push(eventEntry);
              } else {
                eventsMap[key] = {
                  isEvent: true,
                  events: [eventEntry],
                };
              }
            }
            // Advance by 30 minutes
            curM += 30;
            if (curM >= 60) {
              curH++;
              curM = 0;
            }
          }
        });

        this.gridAvailability = availMap;
        this.gridEvents = eventsMap;
      } catch (error) {
        console.error('Error loading grid availability:', error);
        this.$emit('notification', 'Error loading availability data', 'error');
      }
    },
    getSlotClass(date, time) {
      const key = this.getSlotKey(date, time);

      if (this.gridActiveTab === 'events') {
        // Events tab: show events, empty slots are plain
        const ev = this.gridEvents[key];
        if (ev && ev.isEvent) {
          const overlap =
            ev.events && ev.events.length > 1 ? ' slot-event-overlap' : '';
          return 'slot-event' + overlap;
        }
        return 'slot-empty';
      }

      // Availability tab: show availability only, no events
      const slot = this.gridAvailability[key];
      if (!slot) return 'slot-unavailable';
      if (slot.reserved_by) return 'slot-reserved';
      if (!slot.available) return 'slot-unavailable';
      const purpose = slot.purpose || 'anything';
      const state = this.purposeStates.find((s) => s.key === purpose);
      return state ? state.cssClass : 'slot-anything';
    },
    getSlotText(date, time) {
      const key = this.getSlotKey(date, time);

      if (this.gridActiveTab === 'events') {
        const ev = this.gridEvents[key];
        if (ev && ev.isEvent) {
          return ev.events[0].title || 'Event';
        }
        return '';
      }

      // Availability tab
      const slot = this.gridAvailability[key];
      if (!slot) return '';
      if (slot.reserved_by && slot.location) return slot.location;
      if (slot.available) {
        const purpose = slot.purpose || 'anything';
        const state = this.purposeStates.find((s) => s.key === purpose);
        return state ? state.label : '';
      }
      return '';
    },
    showSlotTooltip(date, time, event) {
      const key = this.getSlotKey(date, time);
      const ev = this.gridEvents[key];
      if (!ev || !ev.isEvent) {
        this.hoveredSlot = null;
        return;
      }
      const container = event.target.closest('.grid-container');
      const containerRect = container.getBoundingClientRect();

      const mouseX = event.clientX;
      const mouseY = event.clientY;
      const offset = 5;

      const containerMidY = containerRect.top + containerRect.height / 2;
      const containerMidX = containerRect.left + containerRect.width / 2;

      // Horizontal: show tooltip to the right or left of mouse
      const anchorRight = mouseX > containerMidX;
      const hPos = anchorRight
        ? { anchorRight: true, right: window.innerWidth - mouseX + offset }
        : { anchorRight: false, x: mouseX + offset };

      // Vertical: show tooltip above or below mouse
      if (mouseY > containerMidY) {
        const bottomFromViewport = window.innerHeight - mouseY + offset;
        this.hoveredSlot = {
          date,
          time,
          ...hPos,
          bottom: bottomFromViewport,
          anchorBottom: true,
        };
      } else {
        const y = mouseY + offset;
        this.hoveredSlot = {
          date,
          time,
          ...hPos,
          y,
          anchorBottom: false,
        };
      }
    },
    hideSlotTooltip() {
      this.hoveredSlot = null;
    },
    async toggleSlot(date, time, event) {
      // In events tab, clicking a slot with an event opens it for editing
      if (this.gridActiveTab === 'events') {
        const key = this.getSlotKey(date, time);
        const ev = this.gridEvents[key];
        if (ev && ev.isEvent && ev.events.length > 0) {
          const gridEvent = ev.events[0];
          const matchingEvent = this.events.find(
            (e) => e.title === gridEvent.title,
          );
          if (matchingEvent) {
            this.editEvent(matchingEvent);
            this.showGridEventForm = true;
            this.showEventModal = false;
          }
        }
        return;
      }

      // Check for shift-click range selection
      if (event && event.shiftKey && this.lastClickedCell) {
        // Vertical range: same date, different times
        if (this.lastClickedCell.date === date) {
          await this.toggleSlotRangeVertical(
            date,
            this.lastClickedCell.time,
            time,
          );
          return;
        }
        // Horizontal range: same time, different dates
        if (this.lastClickedCell.time === time) {
          await this.toggleSlotRangeHorizontal(
            time,
            this.lastClickedCell.date,
            date,
          );
          return;
        }
      }

      const key = this.getSlotKey(date, time);
      const currentSlot = this.gridAvailability[key];

      // Don't allow toggling reserved slots
      if (currentSlot && currentSlot.reserved_by) {
        this.$emit(
          'notification',
          'Cannot modify reserved time slots',
          'warning',
        );
        return;
      }

      // If this cell is not the currently selected cell, just select it
      const isAlreadySelected =
        this.selectedCell &&
        this.selectedCell.date === date &&
        this.selectedCell.time === time;

      if (!isAlreadySelected) {
        this.selectedCell = { date, time };
        this.lastClickedCell = { date, time };
        return;
      }

      // Cell is already selected — cycle through purpose states
      const currentPurpose =
        currentSlot && currentSlot.available
          ? currentSlot.purpose || 'anything'
          : 'unavailable';
      const currentIdx = this.purposeStates.findIndex(
        (s) => s.key === currentPurpose,
      );
      const nextIdx = (currentIdx + 1) % this.purposeStates.length;
      const nextState = this.purposeStates[nextIdx];

      try {
        await axios.post('/api/scheduling/availability', {
          slot_date: date,
          slot_time: time + ':00',
          available: nextState.available,
          purpose: nextState.available ? nextState.key : null,
        });

        // Update local state
        this.gridAvailability[key] = {
          available: nextState.available,
          purpose: nextState.available ? nextState.key : null,
          location: null,
          reserved_by: null,
        };
        // Force reactivity
        this.gridAvailability = { ...this.gridAvailability };
      } catch (error) {
        console.error('Error toggling slot:', error);
        this.$emit('notification', 'Error updating availability', 'error');
      }
    },
    async toggleSlotRangeVertical(date, time1, time2) {
      // Find the indices of the two times in gridTimeSlots
      const timeSlots = this.gridTimeSlots;
      const idx1 = timeSlots.findIndex((s) => s.time === time1);
      const idx2 = timeSlots.findIndex((s) => s.time === time2);

      if (idx1 === -1 || idx2 === -1) return;

      const startIdx = Math.min(idx1, idx2);
      const endIdx = Math.max(idx1, idx2);

      // Determine the target state based on the originally clicked cell (time1)
      // The original cell was already toggled, so we want to MATCH its current state
      const originalKey = this.getSlotKey(date, time1);
      const originalSlot = this.gridAvailability[originalKey];
      const targetAvailable = originalSlot ? originalSlot.available : true;
      const targetPurpose = originalSlot ? originalSlot.purpose : 'anything';

      // Build list of slots to update
      const slots = [];
      for (let i = startIdx; i <= endIdx; i++) {
        const slotTime = timeSlots[i].time;
        const key = this.getSlotKey(date, slotTime);
        const current = this.gridAvailability[key];

        // Skip reserved slots
        if (current && current.reserved_by) continue;
        // Skip slots that already have an active purpose
        if (current && current.available) continue;

        slots.push({
          slot_date: date,
          slot_time: slotTime + ':00',
          available: targetAvailable,
          purpose: targetAvailable ? targetPurpose : null,
        });
      }

      if (slots.length === 0) return;

      // Save previous state for undo
      const previousStates = slots.map((slot) => {
        const k = this.getSlotKey(date, slot.slot_time.substring(0, 5));
        return {
          key: k,
          slot_date: slot.slot_date,
          slot_time: slot.slot_time,
          state: this.gridAvailability[k]
            ? { ...this.gridAvailability[k] }
            : null,
        };
      });

      try {
        await axios.post('/api/scheduling/availability/bulk', { slots });

        this.lastRangeFill = previousStates;

        // Update local state
        slots.forEach((slot) => {
          const key = this.getSlotKey(date, slot.slot_time.substring(0, 5));
          this.gridAvailability[key] = {
            available: targetAvailable,
            purpose: targetAvailable ? targetPurpose : null,
            location: null,
            reserved_by: null,
          };
        });
        this.gridAvailability = { ...this.gridAvailability };

        // Update last clicked cell to the end of the range
        this.lastClickedCell = { date, time: time2 };
        this.selectedCell = { date, time: time2 };
      } catch (error) {
        console.error('Error toggling slot range:', error);
        this.$emit(
          'notification',
          'Error updating availability range',
          'error',
        );
      }
    },
    async toggleSlotRangeHorizontal(time, date1, date2) {
      // Find the indices of the two dates in gridWeekDays
      const weekDays = this.gridWeekDays;
      const idx1 = weekDays.findIndex((d) => d.date === date1);
      const idx2 = weekDays.findIndex((d) => d.date === date2);

      if (idx1 === -1 || idx2 === -1) return;

      const startIdx = Math.min(idx1, idx2);
      const endIdx = Math.max(idx1, idx2);

      // Determine the target state based on the originally clicked cell (date1)
      const originalKey = this.getSlotKey(date1, time);
      const originalSlot = this.gridAvailability[originalKey];
      const targetAvailable = originalSlot ? originalSlot.available : true;
      const targetPurpose = originalSlot ? originalSlot.purpose : 'anything';

      // Build list of slots to update
      const slots = [];
      for (let i = startIdx; i <= endIdx; i++) {
        const slotDate = weekDays[i].date;
        const key = this.getSlotKey(slotDate, time);
        const current = this.gridAvailability[key];

        // Skip reserved slots
        if (current && current.reserved_by) continue;
        // Skip slots that already have an active purpose
        if (current && current.available) continue;

        slots.push({
          slot_date: slotDate,
          slot_time: time + ':00',
          available: targetAvailable,
          purpose: targetAvailable ? targetPurpose : null,
        });
      }

      if (slots.length === 0) return;

      // Save previous state for undo
      const previousStates = slots.map((slot) => {
        const k = this.getSlotKey(slot.slot_date, time);
        return {
          key: k,
          slot_date: slot.slot_date,
          slot_time: slot.slot_time,
          state: this.gridAvailability[k]
            ? { ...this.gridAvailability[k] }
            : null,
        };
      });

      try {
        await axios.post('/api/scheduling/availability/bulk', { slots });

        this.lastRangeFill = previousStates;

        // Update local state
        slots.forEach((slot) => {
          const key = this.getSlotKey(slot.slot_date, time);
          this.gridAvailability[key] = {
            available: targetAvailable,
            purpose: targetAvailable ? targetPurpose : null,
            location: null,
            reserved_by: null,
          };
        });
        this.gridAvailability = { ...this.gridAvailability };

        // Update last clicked cell to the end of the range
        this.lastClickedCell = { date: date2, time };
        this.selectedCell = { date: date2, time };
      } catch (error) {
        console.error('Error toggling slot range:', error);
        this.$emit(
          'notification',
          'Error updating availability range',
          'error',
        );
      }
    },
    async handleGridKeydown(event) {
      if (!this.showGridView) return;

      // ESC key: deselect current cell
      if (event.key === 'Escape') {
        this.selectedCell = null;
        this.lastClickedCell = null;
        return;
      }

      // Ctrl+Z: undo last range fill
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key === 'z' &&
        this.lastRangeFill
      ) {
        event.preventDefault();
        const undoSlots = this.lastRangeFill.map((entry) => ({
          slot_date: entry.slot_date,
          slot_time: entry.slot_time,
          available: entry.state ? entry.state.available || false : false,
          purpose: entry.state ? entry.state.purpose || null : null,
        }));
        try {
          await axios.post('/api/scheduling/availability/bulk', {
            slots: undoSlots,
          });
          this.lastRangeFill.forEach((entry) => {
            this.gridAvailability[entry.key] = entry.state || {
              available: false,
              purpose: null,
              location: null,
              reserved_by: null,
            };
          });
          this.gridAvailability = { ...this.gridAvailability };
          this.lastRangeFill = null;
        } catch (error) {
          console.error('Error undoing range fill:', error);
          this.$emit('notification', 'Error undoing range fill', 'error');
        }
        return;
      }

      if (!this.selectedCell) return;

      // Arrow key navigation
      if (
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)
      ) {
        event.preventDefault();
        const days = this.gridWeekDays;
        const times = this.gridTimeSlots;
        let dayIdx = days.findIndex((d) => d.date === this.selectedCell.date);
        let timeIdx = times.findIndex((t) => t.time === this.selectedCell.time);
        if (dayIdx === -1 || timeIdx === -1) return;

        if (event.key === 'ArrowUp') {
          timeIdx--;
          if (timeIdx < 0) timeIdx = times.length - 1;
        } else if (event.key === 'ArrowDown') {
          timeIdx++;
          if (timeIdx >= times.length) timeIdx = 0;
        } else if (event.key === 'ArrowLeft') {
          dayIdx--;
          if (dayIdx < 0) dayIdx = days.length - 1;
        } else if (event.key === 'ArrowRight') {
          dayIdx++;
          if (dayIdx >= days.length) dayIdx = 0;
        }

        const newCell = { date: days[dayIdx].date, time: times[timeIdx].time };
        this.selectedCell = newCell;
        this.lastClickedCell = newCell;
        return;
      }

      const { date, time } = this.selectedCell;
      const key = this.getSlotKey(date, time);
      const currentSlot = this.gridAvailability[key];

      // Don't modify reserved slots
      if (currentSlot && currentSlot.reserved_by) return;

      if (event.key === 'Delete') {
        try {
          await axios.post('/api/scheduling/availability', {
            slot_date: date,
            slot_time: time + ':00',
            available: false,
            purpose: null,
          });

          this.gridAvailability[key] = {
            available: false,
            purpose: null,
            location: null,
            reserved_by: null,
          };
          this.gridAvailability = { ...this.gridAvailability };
        } catch (error) {
          console.error('Error clearing slot:', error);
          this.$emit('notification', 'Error clearing slot', 'error');
        }
        return;
      }

      // Letter key shortcut: match first letter of purpose fullLabel
      const pressed = event.key.toLowerCase();
      if (pressed.length !== 1 || pressed < 'a' || pressed > 'z') return;

      const matches = this.purposeStates.filter(
        (s) => s.fullLabel.charAt(0).toLowerCase() === pressed,
      );
      if (matches.length === 0) return;

      // If multiple matches (e.g. L for Lock Batteries / Lease Signing),
      // cycle through them based on current slot value
      let matchedState = matches[0];
      if (matches.length > 1 && currentSlot && currentSlot.available) {
        const curIdx = matches.findIndex((s) => s.key === currentSlot.purpose);
        if (curIdx !== -1) {
          matchedState = matches[(curIdx + 1) % matches.length];
        }
      }

      try {
        await axios.post('/api/scheduling/availability', {
          slot_date: date,
          slot_time: time + ':00',
          available: matchedState.available,
          purpose: matchedState.available ? matchedState.key : null,
        });

        this.gridAvailability[key] = {
          available: matchedState.available,
          purpose: matchedState.available ? matchedState.key : null,
          location: null,
          reserved_by: null,
        };
        this.gridAvailability = { ...this.gridAvailability };
      } catch (error) {
        console.error('Error setting slot purpose:', error);
        this.$emit('notification', 'Error updating slot', 'error');
      }
    },
    async autoFillWeek() {
      const slots = [];

      // All 7 days of the week (unfiltered by current view)
      const allDays = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(this.gridWeekStart);
        date.setDate(date.getDate() + i);
        allDays.push({
          dayIndex: i,
          date: this.formatDateForKey(date),
        });
      }

      for (const day of allDays) {
        // Mon-Fri only (dayIndex 1-5)
        if (day.dayIndex < 1 || day.dayIndex > 5) continue;

        for (const timeSlot of this.gridTimeSlots) {
          // 8:30am to 3:30pm (covers 8:30-4:00 in 30-min blocks)
          if (timeSlot.time < '08:30' || timeSlot.time > '16:00') continue;

          const key = this.getSlotKey(day.date, timeSlot.time);
          const current = this.gridAvailability[key];
          // Skip reserved slots
          if (current && current.reserved_by) continue;

          slots.push({
            slot_date: day.date,
            slot_time: timeSlot.time + ':00',
            available: true,
            purpose: 'anything',
          });
        }
      }

      if (slots.length === 0) return;

      try {
        await axios.post('/api/scheduling/availability/bulk', { slots });

        // Update local state
        slots.forEach((slot) => {
          const key = this.getSlotKey(
            slot.slot_date,
            slot.slot_time.substring(0, 5),
          );
          this.gridAvailability[key] = {
            available: true,
            purpose: 'anything',
            location: null,
            reserved_by: null,
          };
        });
        this.gridAvailability = { ...this.gridAvailability };

        this.$emit('notification', 'Week auto-filled successfully', 'success');
      } catch (error) {
        console.error('Error auto-filling week:', error);
        this.$emit('notification', 'Error auto-filling week', 'error');
      }
    },
    async clearWeek() {
      const slots = [];

      for (const day of this.gridWeekDays) {
        for (const timeSlot of this.gridTimeSlots) {
          const key = this.getSlotKey(day.date, timeSlot.time);
          const current = this.gridAvailability[key];
          // Skip reserved slots
          if (current && current.reserved_by) continue;

          slots.push({
            slot_date: day.date,
            slot_time: timeSlot.time + ':00',
            available: false,
            purpose: null,
          });
        }
      }

      if (slots.length === 0) return;

      try {
        await axios.post('/api/scheduling/availability/bulk', { slots });

        // Update local state
        slots.forEach((slot) => {
          const key = this.getSlotKey(
            slot.slot_date,
            slot.slot_time.substring(0, 5),
          );
          this.gridAvailability[key] = {
            available: false,
            purpose: null,
            location: null,
            reserved_by: null,
          };
        });
        this.gridAvailability = { ...this.gridAvailability };

        this.$emit('notification', 'Week cleared successfully', 'success');
      } catch (error) {
        console.error('Error clearing week:', error);
        this.$emit('notification', 'Error clearing week', 'error');
      }
    },
  },
  mounted() {
    // Fetch user roles and current user info
    this.fetchUserRoles();
    this.fetchUserInfo();

    // Add keyboard listener for Delete key on grid
    document.addEventListener('keydown', this.handleGridKeydown);

    // Check for URL parameters to open specific date/event
    const urlParams = new URLSearchParams(window.location.search);
    const urlMonth = urlParams.get('month');
    const urlYear = urlParams.get('year');
    const urlDay = urlParams.get('day');
    const chip = calendarChipDate(
      `#calendar-${urlMonth}-${urlYear}-${urlDay}`,
    );
    const openList = urlParams.get('view') === 'list' && !!chip;

    if (chip) {
      const targetDate = localDateFromYmd(chip.ymd);
      this.currentDate = new Date(chip.year, chip.month - 1, 1);
      this.selectedDate = targetDate;
      if (openList) {
        this.listFocusYmd = chip.ymd;
        this.showListView = true;
        this.showEventsPanel = false;
      }

      this.loadEvents().then(() => {
        this.restoreEventFormDraft();
        if (this.listFocusYmd) {
          this.showListView = true;
          this.showEventsPanel = false;
          this.$nextTick(() => this.scrollListToFocus());
        } else if (!this.showEventModal) {
          this.selectedDate = targetDate;
          this.showEventsPanel = true;
        }
      });
    }

    // Detect if we're in mobile mode by checking viewport width or route
    const isMobileViewport = window.innerWidth <= 768;
    const isMobileRoute = this.$route && this.$route.path === '/calendar';

    // If accessed via the /calendar route, show the calendar as a standalone
    // page (not just a modal). Previously this required a mobile-sized
    // viewport, which left the page blank on desktop widths.
    if (isMobileRoute) {
      this.isMobileStandalone = true;
      console.log(
        '📱 [CALENDAR] Standalone mode detected (mobileViewport=',
        isMobileViewport,
        ')',
      );
      // Load events when in standalone mode
      if (!urlMonth && !urlYear && !urlDay) {
        // Only load events if we didn't already load them from URL params
        this.loadEvents().then(() => {
          this.restoreEventFormDraft();
          // Auto-open list view if it's the saved default
          if (this.calendarDefaultView === 'list' && !this.showEventModal) {
            this.showListView = true;
          }
        });
      }
    }

    this._onCalendarResyncHintResize = () => {
      if (this.showCalendarResyncHint) this.placeCalendarResyncHint();
    };
    window.addEventListener('resize', this._onCalendarResyncHintResize);
    if (this.isCalendarVisible) this.placeCalendarResyncHint();

    // Add touch event listeners for swipe gestures on mobile
    if (this.isMobile) {
      this.$nextTick(() => {
        const calendarGrid = this.$refs.calendarGrid;
        if (calendarGrid) {
          calendarGrid.addEventListener('touchstart', this.handleTouchStart, {
            passive: true,
          });
          calendarGrid.addEventListener('touchend', this.handleTouchEnd, {
            passive: true,
          });
        }
      });
    }
  },
  beforeUnmount() {
    this.cancelRefreshHold();
    this.persistEventFormDraft();
    if (this.eventFormDraftTimer) {
      clearTimeout(this.eventFormDraftTimer);
    }
    // Clean up keyboard listener
    document.removeEventListener('keydown', this.handleGridKeydown);
    if (this._onCalendarResyncHintResize) {
      window.removeEventListener('resize', this._onCalendarResyncHintResize);
    }
    // Clean up touch event listeners
    if (this.isMobile) {
      const calendarGrid = this.$refs.calendarGrid;
      if (calendarGrid) {
        calendarGrid.removeEventListener('touchstart', this.handleTouchStart);
        calendarGrid.removeEventListener('touchend', this.handleTouchEnd);
      }
    }
  },
};
</script>

<style scoped>
.calendar-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
}

.calendar-content {
  background: #dbdbdb;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  width: 100%;
  /* max-height: 90vh; */
  overflow: visible;
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
  transition: width 0.3s ease;
}

/* Desktop-only calendar content sizing */
@media screen and (min-width: 769px) {
  .calendar-content {
    min-width: 500px;
    max-width: 520px;
  }
}

/* Expand calendar content when events panel is shown */
.calendar-content.with-events-panel {
  width: 900px !important; /* 500px calendar + 400px events panel */
  min-width: 900px;
}

.calendar-main {
  display: flex;
  flex: 1;
  position: relative;
}

.calendar-section {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  box-sizing: border-box;
}

/* Desktop-only calendar section sizing */
@media screen and (min-width: 769px) {
  .calendar-section {
    width: 500px !important;
    min-width: 475px;
    max-width: 475px;
    margin-left: 10px;
  }
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 18px;
  border-bottom: 1px solid #eee;
  background-color: #6e5875;
  color: white;
}

.calendar-header h3 {
  margin: 0;
  font-size: 1.4rem;
}

.calendar-header-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.shared-calendar-btn {
  background: #907793;
  color: white;
  border: 1px solid #d9d5d7;
  padding: 10px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.shared-calendar-btn i {
  color: white;
}

.shared-calendar-btn:hover {
  background: #b8a8c7;
  transform: translateY(-1px);
}

.shared-calendar-btn.active {
  background: #8878a7;
  border-color: #727272;
  box-shadow: 0 2px 4px rgba(168, 152, 183, 0.3);
}

.calendar-body {
  display: flex;
  flex: 1;
  overflow: hidden;
  position: relative;
}

.calendar-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  /* min-width: 500px; */
}

.calendar-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
}

/* Desktop-only calendar nav padding */
@media screen and (min-width: 769px) {
  .calendar-nav {
    padding: 15px 0px;
  }
}

.calendar-nav h4 {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
}

.nav-btn {
  padding: 8px 12px;
  border: none;
  background: #f8f9fa;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.nav-btn:hover {
  background: #e9ecef;
}

.calendar-grid {
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 90vw;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
  max-width: 92vw;
  border-radius: 5px;
  margin-bottom: 5px;
}

/* Desktop-only calendar weekdays margin */
@media screen and (min-width: 769px) {
  .calendar-weekdays {
    margin-left: 0px;
  }
}

.weekday {
  padding: 12px;
  text-align: center;
  font-weight: 600;
  color: #666;
  font-size: 0.85rem;
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: repeat(6, 1fr);
  flex: 1;
}

.calendar-day {
  position: relative;
  border: 1px solid #eee;
  padding: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  min-height: 60px;
  font-size: 0.9rem;
  background: #ffffff;
}

.calendar-day:hover {
  background-color: #f0f8ff;
}

.calendar-day.other-month {
  color: #ccc;
  background-color: #fafafa;
}

.calendar-day.today {
  background-color: #e3f2fd;
  font-weight: bold;
  color: #1976d2;
}

.calendar-day.selected {
  background-color: #2196f3;
  color: white;
}

.event-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  color: white;
  font-size: 0.7rem;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  font-weight: 600;
}

.event-badge.personal-badge {
  border-radius: 2px; /* Square for personal events */
}

.calendar-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px;
  margin-top: 4px;
  border-top: 1px solid #eee;
  background: #f8f9fa;
  border-radius: 5px;
}

.calendar-filter-select {
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  font-size: 0.8em;
  color: #333;
  cursor: pointer;
  outline: none;
}

.calendar-filter-select:focus {
  border-color: #007bff;
}

/* Desktop-only calendar footer spacing */
@media screen and (min-width: 769px) {
  .calendar-footer {
    margin-top: 11px;
    margin-bottom: 9px;
  }
}

/* Events Panel */
.events-panel {
  position: absolute;
  top: 0;
  left: 500px; /* Position adjacent to calendar section */
  width: 400px; /* Increased from 250px to 400px */
  height: 100%;
  background: white;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  border-radius: 0 8px 8px 0;
  display: flex;
  flex-direction: column;
  opacity: 0;
  visibility: hidden;
  transform: translateX(
    20px
  ); /* Start slightly off-screen for smooth animation */
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease,
    transform 0.3s ease;
  z-index: 10;
}

.events-panel.show-panel {
  opacity: 1;
  visibility: visible;
  transform: translateX(0); /* Slide into place */
}

.events-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #eee;
  background: white;
  gap: 10px;
}

.events-header h4 {
  margin: 0;
  font-size: 0.95rem;
  color: #333;
  flex: 1;
  text-align: center;
}

.events-header button {
  padding: 6px 8px;
  font-size: 0.85rem;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #f3f4f6;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 30px;
  height: 30px;
  transition: background 0.15s;
}

.events-header button:hover {
  background: #f3f4f6;
  color: #374151;
  border-color: #d1d5db;
  transform: none;
  box-shadow: none;
}

.close-events-btn span {
  font-weight: bold;
  line-height: 1;
}

.events-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  padding-bottom: 60px;
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.events-list::-webkit-scrollbar {
  display: none;
}

.no-events {
  text-align: center;
  color: #666;
  font-style: italic;
  padding: 20px;
}

.event-item {
  background: white;
  border-radius: 6px;
  padding: 10px;
  /* margin-bottom: 8px; */
  transition: all 0.2s;
  border-left: 3px solid transparent;
  border-bottom: 1px solid #ddd;
}

.event-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.event-content {
  cursor: pointer;
}

.event-text-content {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.event-location {
  font-size: 0.8rem;
  color: #888;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.event-location i {
  width: 12px;
}

.location-btn {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 0.75rem;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
}

.location-btn:hover {
  background: rgba(0, 123, 255, 0.1);
  color: #0056b3;
  transform: scale(1.05);
}

.event-time {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 4px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.event-visibility-icon {
  font-size: 0.75rem;
  color: #999;
  cursor: help;
}

.event-main {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.event-color-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 6px;
}

.event-title {
  font-size: 1rem;
  color: #333;
  flex: 1;
  line-height: 1.1;
  font-weight: bold;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.event-purpose-label {
  font-size: 0.75rem;
  color: #999;
  white-space: nowrap;
  flex-shrink: 0;
}

.event-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  min-width: 0;
  overflow: hidden;
}

.event-description {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 4px;
  line-height: 1.4;
  display: block;
}

.event-description-wrapper {
  position: relative;
}

.description-clamped {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.description-more-btn {
  background: none;
  border: none;
  color: #4a90d9;
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0;
  margin-top: 2px;
}

.description-more-btn:hover {
  text-decoration: underline;
}

/* Markdown elements in event descriptions */
:deep(.event-description h1),
:deep(.event-description h2),
:deep(.event-description h3),
:deep(.event-description h4),
:deep(.event-description h5),
:deep(.event-description h6) {
  color: #444;
  margin-top: 0.5em;
  margin-bottom: 0.3em;
  font-size: 1em;
  font-weight: 600;
}

:deep(.event-description p) {
  margin: 0.3em 0;
}

:deep(.event-description ul),
:deep(.event-description ol) {
  margin: 0.3em 0;
  padding-left: 1.5em;
}

:deep(.event-description li) {
  margin-bottom: 0.2em;
}

:deep(.event-description strong) {
  font-weight: 600;
  color: #333;
}

:deep(.event-description em) {
  font-style: italic;
  color: #555;
}

:deep(.event-description code) {
  background: #f4f4f4;
  padding: 1px 3px;
  border-radius: 2px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85em;
}

:deep(.event-description pre) {
  background: #f4f4f4;
  padding: 0.5em;
  border-radius: 4px;
  overflow-x: auto;
  margin: 0.3em 0;
}

:deep(.event-description pre code) {
  background: transparent;
  padding: 0;
}

:deep(.event-description .done-checkmark) {
  color: #28a745;
  font-weight: bold;
  padding-right: 2px;
}

/* Phone link styling - use :deep() to target dynamically generated content */
:deep(.event-description .phone-link) {
  color: #007bff;
  text-decoration: none;
  padding: 3px 6px;
  border-radius: 3px;
  transition: all 0.2s ease;
  background: rgba(0, 123, 255, 0.08);
  font-size: 0.9em;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

:deep(.event-description .phone-link i) {
  font-size: 0.85em;
}

:deep(.event-description .phone-link:hover) {
  background: rgba(0, 123, 255, 0.15);
  transform: translateY(-1px);
}

:deep(.event-description .phone-link:active) {
  transform: translateY(0);
  background: rgba(0, 123, 255, 0.2);
}

.meeting-btn {
  background: none;
  border: none;
  color: #007bff;
  font-size: 0.8rem;
  padding: 4px;
  cursor: pointer;
  border-radius: 3px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.meeting-btn:hover {
  background: #e3f2fd;
  color: #0056b3;
  transform: scale(1.1);
}

.meeting-btn:active {
  transform: scale(0.95);
}

.calendar-link-btn {
  background: none;
  border: none;
  color: #28a745;
  font-size: 0.8rem;
  padding: 4px;
  cursor: pointer;
  border-radius: 3px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: 4px;
}

.calendar-link-btn:hover {
  background: #e8f5e8;
  color: #1e7e34;
  transform: scale(1.1);
}

.calendar-link-btn:active {
  transform: scale(0.95);
}

/* Event Form Flyout */
.event-form-flyout {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  border-left: 1px solid #ddd;
  z-index: 20;
  display: flex;
  flex-direction: column;
  animation: slideInFromRight 0.3s ease;
}

@keyframes slideInFromRight {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

.event-form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
}

.event-form-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
}

.event-form-body {
  padding: 15px;
  overflow-y: auto;
  flex: 1;
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.event-form-body::-webkit-scrollbar {
  display: none;
}

.event-form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 15px;
  border-top: 1px solid #eee;
  background: #f8f9fa;
}

.delete-btn {
  background: #dc3545;
  color: white;
}

.delete-btn:hover {
  background: #c82333;
}

.copy-btn {
  background: #17a2b8;
  color: white;
  margin-right: auto;
}

.copy-btn:hover {
  background: #138496;
}

.form-group {
  margin-bottom: 15px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
}

.form-row .form-group {
  margin-bottom: 0;
}

.form-row input[type='date'],
.form-row input[type='time'] {
  font-size: 0.75rem;
  padding: 6px 8px;
  width: 100%;
  max-width: 110px;
  text-align: center;
}

/* Make date input more compact */
.form-row input[type='date']::-webkit-calendar-picker-indicator {
  margin-left: 2px;
}

.date-nav-btn {
  background: none;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  padding: 4px 6px;
  color: #555;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.date-nav-btn:hover {
  background: #e9ecef;
  color: #333;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #333;
  font-size: 0.9rem;
}

.form-group label.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: normal;
}

.form-group label.checkbox-label input[type='checkbox'] {
  width: auto;
  cursor: pointer;
  margin: 0;
}

.form-group label.checkbox-label span {
  user-select: none;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.85rem;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #2196f3;
}

.color-picker {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.color-option {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.color-option-letter {
  color: white;
  font-size: 10px;
  font-weight: bold;
  line-height: 1;
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.7);
  pointer-events: none;
}

.color-option:hover {
  transform: scale(1.1);
}

.color-option.selected {
  border-color: #333;
  transform: scale(1.1);
}

.cancel-btn {
  background: #6c757d;
  color: white;
}

.cancel-btn:hover {
  background: #5a6268;
}

.save-btn {
  background: #28a745;
  color: white;
}

.save-btn:hover {
  background: #218838;
}

.menu-btn {
  padding: 8px 16px;
  border: 1pt solid #d6d6d6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 5px;
  background: #b9a7be;
}

.menu-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.calendar-refresh-btn {
  touch-action: manipulation;
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
}

/* Delete Confirmation Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  padding: 20px;
  box-sizing: border-box;
}

.delete-confirmation-modal {
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  width: 500px;
  max-width: 90vw;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.delete-confirmation-modal .modal-body {
  height: auto !important;
  min-height: auto !important;
  flex: none;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
}

.modal-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: #6b7280;
  border-radius: 4px;
}

.modal-close-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 1.5rem;
  flex: 1;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.delete-warning {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 0.5rem 0;
}

.delete-warning i {
  color: #f59e0b;
  font-size: 1.5rem;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.delete-warning h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
}

.delete-warning p {
  margin: 0.5rem 0;
  color: #6b7280;
}

.warning-text {
  color: #dc2626 !important;
  font-weight: 500;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
}

.btn-secondary {
  background-color: #6b7280;
  color: white;
  border: 1px solid #6b7280;
}

.btn-secondary:hover {
  background-color: #4b5563;
}

.btn-danger {
  background-color: #dc2626;
  color: white;
  border: 1px solid #dc2626;
}

.btn-danger:hover {
  background-color: #b91c1c;
  border-color: #b91c1c;
}

/* ===== Scheduling Grid Styles ===== */
.grid-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.grid-modal-wrapper {
  display: flex;
  align-items: stretch;
  max-height: 90vh;
  gap: 0;
}

.grid-modal {
  background: white;
  border-radius: 12px;
  width: 630px;
  flex-shrink: 0;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.grid-modal-wrapper .grid-modal:not(:only-child) {
  border-radius: 12px 0 0 12px;
}

.grid-event-flyout {
  background: white;
  width: 400px;
  min-width: 380px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  border-left: 2px solid #6e5875;
  border-radius: 0 12px 12px 0;
  box-shadow: 4px 4px 20px rgba(0, 0, 0, 0.15);
  animation: slideInFromRight 0.25s ease;
}

.grid-event-flyout .event-form-header {
  background: #6e5875;
  color: white;
  border-radius: 0 12px 0 0;
}

.grid-event-flyout .event-form-header h3 {
  color: white;
}

.grid-event-flyout .event-form-header .menu-btn {
  background: #907793;
  color: white;
  border: 1px solid #d9d5d7;
}

.grid-event-flyout .event-form-footer {
  border-radius: 0 0 12px 0;
}

.grid-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  background-color: #6e5875;
  color: white;
  border-radius: 12px 12px 0 0;
}

.grid-tabs {
  display: flex;
  background: #f0edf1;
  border-bottom: 1px solid #ddd;
}

.grid-tab {
  flex: 1;
  padding: 10px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.9rem;
  color: #666;
  border-bottom: 3px solid transparent;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.grid-tab:hover {
  color: #6e5875;
  background: #e8e0eb;
}

.grid-tab.active {
  color: #6e5875;
  border-bottom-color: #6e5875;
  font-weight: 600;
  background: #fff;
}

.slot-empty {
  background: #fff;
}

.grid-header-title {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.grid-header h3 {
  margin: 0;
  font-size: 1.3rem;
  color: white;
}

.grid-header-range {
  font-size: 0.85rem;
  color: #d9d5d7;
  font-weight: 400;
}

.grid-header .menu-btn {
  background: #907793;
  color: white;
  border: 1px solid #d9d5d7;
}

.grid-nav {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
}

.grid-nav .fill-btn {
  background: #28a745;
  color: white;
  margin-left: auto;
}

.grid-nav .fill-btn:hover {
  background: #218838;
}

.grid-nav .clear-btn {
  background: #dc3545;
  color: white;
}

.grid-nav .clear-btn:hover {
  background: #c82333;
}

.grid-date-picker {
  padding: 5px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
}

.grid-container {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 10px;
}

.grid-weekdays {
  display: grid;
  /* grid-template-columns set dynamically via inline style */
  grid-template-columns: 55px repeat(7, 1fr);
  gap: 1px;
  position: sticky;
  top: 0;
  background: white;
  z-index: 5;
  padding-bottom: 3px;
  border-bottom: 1px solid #eee;
}

.grid-time-header {
  min-width: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.grid-mode-btn {
  background: #6e5875;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 6px;
  font-size: 0.65rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.grid-mode-btn:hover {
  background: #5a4763;
}

.grid-weekday {
  text-align: center;
  padding: 5px;
}

.grid-day-name {
  font-weight: 600;
  font-size: 0.75rem;
  color: #333;
}

.grid-day-date {
  font-size: 0.65rem;
  color: #666;
}

.grid-body {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 2px;
}

.grid-row {
  display: grid;
  grid-template-columns: 55px repeat(7, 1fr);
  gap: 1px;
}

.grid-time-label {
  font-size: 0.65rem;
  color: #666;
  padding: 1px;
  text-align: right;
  padding-right: 4px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.grid-cell {
  min-height: 16px;
  border: 1px solid #ddd;
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  transition: all 0.15s ease;
  padding: 0 1px;
  overflow: hidden;
  min-width: 0;
}

.grid-cell:hover {
  transform: scale(1.02);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.grid-cell-selected {
  outline: 2px solid #6f42c1;
  outline-offset: -2px;
  box-shadow: 0 0 6px rgba(111, 66, 193, 0.4);
  z-index: 1;
}

.grid-today {
  background-color: #f8e8f8;
}

.grid-weekday.grid-today {
  background-color: #f0d4f0;
}

.slot-anything {
  background-color: #d4edda;
  border-color: #28a745;
  color: #155724;
}

.slot-maintenance {
  background-color: #cce5ff;
  border-color: #007bff;
  color: #004085;
}

.slot-lock-batteries {
  background-color: #ffe5cc;
  border-color: #fd7e14;
  color: #804000;
}

.slot-lease-sign {
  background-color: #e8d4f0;
  border-color: #6f42c1;
  color: #3d1a6e;
}

.slot-showing {
  background-color: #d4f0ed;
  border-color: #20c997;
  color: #0d6655;
}

.slot-office-hours {
  background-color: #fff3cd;
  border-color: #e6ac00;
  color: #665200;
}

.slot-inspection {
  background-color: #f0e6ff;
  border-color: #8b5cf6;
  color: #4c1d95;
}

.slot-unavailable {
  background-color: #f8d7da;
  border-color: #dc3545;
  color: #721c24;
}

.slot-reserved {
  background-color: #fff3cd;
  border-color: #ffc107;
  color: #856404;
}

.slot-event {
  background-color: #cce5ff;
  color: #004085;
  cursor: default;
}

.slot-event.slot-event-available {
  border-color: #28a745;
}

.slot-event.slot-event-unavailable {
  border-color: #dc3545;
}

.slot-event.slot-event-overlap {
  background-color: #a3cff0;
  color: #003366;
}

.slot-event:hover {
  transform: none;
  box-shadow: none;
}

.grid-tooltip {
  position: fixed;
  z-index: 1000;
  background: #fff;
  border: 1px solid #007bff;
  border-radius: 6px;
  padding: 8px 12px;
  width: 150px;
  box-sizing: border-box;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  pointer-events: none;
  font-family: 'Segoe UI', Segoe, sans-serif;
  font-size: 0.7rem;
  line-height: 1.3;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
}

.grid-tooltip-event {
  padding: 2px 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  max-width: 100%;
}

.grid-tooltip-title {
  font-weight: 600;
  color: #004085;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
}

.grid-tooltip-desc {
  color: #555;
  margin-top: 2px;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  max-width: 100%;
}

.grid-tooltip-desc :deep(*) {
  max-width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  white-space: pre-wrap;
}

.grid-tooltip-divider {
  border: none;
  border-top: 1px dashed #aaa;
  margin: 6px 0;
}

.legend-item.event .legend-box {
  background-color: #cce5ff;
  border-color: #007bff;
}

.slot-text {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.grid-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-top: 1px solid #eee;
  background: #f8f9fa;
  border-radius: 0 0 12px 12px;
  flex-shrink: 0;
}

.grid-legend {
  display: flex;
  gap: 8px 15px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.85rem;
  color: #666;
}

.legend-box {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid #ddd;
}

.legend-item.anything .legend-box {
  background-color: #d4edda;
  border-color: #28a745;
}

.legend-item.maintenance .legend-box {
  background-color: #cce5ff;
  border-color: #007bff;
}

.legend-item.lock-batteries .legend-box {
  background-color: #ffe5cc;
  border-color: #fd7e14;
}

.legend-item.lease-sign .legend-box {
  background-color: #e8d4f0;
  border-color: #6f42c1;
}

.legend-item.showing .legend-box {
  background-color: #d4f0ed;
  border-color: #20c997;
}

.legend-item.office-hours .legend-box {
  background-color: #fff3cd;
  border-color: #e6ac00;
}

.legend-item.inspection .legend-box {
  background-color: #f0e6ff;
  border-color: #8b5cf6;
}

.legend-item.unavailable .legend-box {
  background-color: #f8d7da;
  border-color: #dc3545;
}

.legend-item.reserved .legend-box {
  background-color: #fff3cd;
  border-color: #ffc107;
}

/* Mobile Responsive Styles */
@media screen and (max-width: 768px) {
  .calendar-popup {
    align-items: flex-start;
  }

  .calendar-content {
    /* width: 95vw !important; */
    min-width: unset !important;
    max-width: 100vw;
    /* margin-top: 5vh; */
    height: 100%;
  }

  .calendar-section {
    width: 100% !important;
    min-width: unset !important;
  }

  /* Scale down calendar elements to fit mobile */
  .calendar-header {
    padding: 12px 8px;
    /* border-top-left-radius: 8px; */
    /* border-top-right-radius: 8px; */
  }

  .calendar-header h3 {
    font-size: 2rem;
    padding-left: 0.5rem;
  }

  /* Circular home button for mobile */
  .menu-btn.home-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #907793;
    border: 1px solid #e1dede;
    color: #eee;
  }

  .menu-btn.home-btn i {
    font-size: 1rem;
  }

  .shared-calendar-btn {
    padding: 8px 6px;
    font-size: 0.65rem;
  }

  .calendar-nav {
    padding: 8px 10px;
    max-width: 90vw;
    margin-left: 2vw;
  }

  .calendar-nav h4 {
    font-size: 1.2rem;
  }

  .calendar-grid {
    max-width: 95vw;
    margin-left: 2vw;
  }

  .weekday {
    padding: 3px 0px;
    font-size: 1rem;
  }

  .calendar-day {
    padding: 6px 5px;
    min-height: 50px;
    font-size: 1rem;
    border: 0.5px solid #808080;
    border-radius: 6px;
  }

  .calendar-days {
    gap: 5px;
    padding-left: 5px;
    padding-right: 5px;
    padding-top: 5px;
  }

  .event-badge {
    width: 17px;
    height: 15px;
    font-size: 0.5rem;
    top: 31px;
    right: 15px;
    padding-top: 2px;
  }

  .calendar-footer {
    padding: 8px 10px;
    max-width: 90vw;
    margin-left: 2vw;
  }

  .menu-btn {
    padding: 10px 10px;
    font-size: 0.8rem;
  }

  .shared-calendar-btn {
    padding: 8px 6px;
    font-size: 0.65rem;
  }

  .nav-btn {
    padding: 8px 18px;
  }

  /* Hide calendar when events panel is shown on mobile */
  .calendar-content.with-events-panel .calendar-section,
  .calendar-content.with-event-form .calendar-section {
    display: none;
  }

  .calendar-main {
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .events-panel {
    width: 100%;
    position: relative;
    top: 0;
    left: 0;
    right: 0;
    /* height: auto; */
    border-radius: 8px;
    overflow: hidden;
    max-height: none;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .event-form-flyout {
    position: absolute;
    width: 100%;
    height: auto;
    /* max-height: 58vh; */
    overflow-y: auto;
  }

  .events-list {
    padding: 5px 10px;
    padding-bottom: 60px;
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .events-panel.show-panel {
    opacity: 1;
    visibility: visible;
    transform: translateX(0);
    overflow-x: hidden;
  }

  /* Mobile Grid Styles */
  .grid-modal-wrapper {
    max-height: 100dvh;
    height: 100dvh;
    width: 100%;
  }

  .grid-modal {
    width: 100%;
    max-width: 100%;
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;
  }

  .grid-header {
    padding: 10px 15px;
  }

  .grid-header h3 {
    font-size: 1.1rem;
  }

  .grid-nav {
    flex-wrap: wrap;
    padding: 8px 10px;
    gap: 8px;
  }

  .grid-nav .menu-btn {
    padding: 6px 10px;
    font-size: 0.7rem;
  }

  .grid-header-range {
    font-size: 0.9em;
  }

  .grid-container {
    padding: 5px;
  }

  .grid-weekdays {
    /* grid-template-columns set dynamically via inline style */
  }

  .grid-row {
    /* grid-template-columns set dynamically via inline style */
  }

  .grid-time-label {
    font-size: 0.65rem;
    padding-right: 4px;
  }

  .grid-cell {
    min-height: 16px;
    font-size: 0.55rem;
  }

  .grid-day-name {
    font-size: 0.65rem;
  }

  .grid-day-date {
    font-size: 0.55rem;
  }

  .grid-footer {
    flex-direction: column;
    gap: 10px;
    padding: 10px 15px;
  }

  .grid-legend {
    gap: 10px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .legend-item {
    font-size: 0.75rem;
  }
}

/* ===== List View Styles ===== */
.list-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.list-modal {
  background: white;
  border-radius: 12px;
  width: 500px;
  max-width: 95vw;
  height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  background-color: #6e5875;
  color: white;
  border-radius: 12px 12px 0 0;
}

.list-header-title {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.list-header h3 {
  margin: 0;
  font-size: 1.3rem;
  color: white;
}

.list-header-range {
  font-size: 0.85rem;
  color: #d9d5d7;
  font-weight: 400;
}

.list-header .menu-btn {
  background: #907793;
  color: white;
  border: 1px solid #d9d5d7;
}

.list-header .menu-btn:hover {
  background: #7a6a7d;
}

.list-search {
  display: flex;
  align-items: center;
  padding: 6px 20px;
  gap: 6px;
  border-bottom: 1px solid #eee;
}

.list-search-input {
  flex: 1;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #ddd;
  padding: 0 10px;
  font-size: 14px;
  outline: none;
}

.list-search-input:focus {
  border-color: #a080a8;
}

.list-search-clear {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  font-size: 16px;
}

.list-search-clear:hover {
  color: #666;
}

.list-search-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.list-print-btn {
  background: #ddd;
  border: none;
  border-radius: 6px;
  height: 28px;
  padding: 0 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  color: #666;
  font-size: 12px;
  flex-shrink: 0;
  white-space: nowrap;
}

.list-print-btn:hover {
  background: #cfcfcf;
}

.list-btn-label {
  font-size: 10px;
  line-height: 1;
  font-weight: 500;
}

.list-add-btn {
  background: #6e5875;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 4px 8px;
  font-size: 16px;
  border-radius: 6px;
  flex-shrink: 0;
}

.list-add-btn:hover {
  background: #5a4862;
}

.list-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.list-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.list-events {
  padding: 10px 15px;
}

.list-date-header {
  font-weight: 600;
  font-size: 0.95rem;
  color: #6e5875;
  padding: 12px 0 8px 0;
  border-bottom: 1px solid #e0d8e3;
  margin-bottom: 8px;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

.list-date-header:first-child {
  padding-top: 0;
}

.list-day-empty {
  padding: 4px 0 12px;
  text-align: left;
}

.list-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  border-top: 1px solid #eee;
  background: #f9f9f9;
  border-radius: 0 0 12px 12px;
}

.list-count {
  font-size: 0.85rem;
  color: #666;
}

.list-footer-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Mobile list view styles */
@media (max-width: 768px) {
  .list-modal {
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .list-header {
    border-radius: 0;
  }

  .list-footer {
    border-radius: 0;
  }
}

/* Specific styling for 915px height devices (e.g., iPhone 11 Pro Max, iPhone XS Max) */
@media screen and (max-width: 768px) and (height: 915px) {
  .calendar-day {
    min-height: 55px;
  }
}

/* View Settings Modal */
.view-settings-modal {
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  width: 420px;
  max-width: 90vw;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.view-settings-modal .modal-body p {
  margin: 0 0 1rem;
  color: #374151;
  font-size: 0.95rem;
  line-height: 1.5;
}

.view-settings-options {
  display: flex;
  gap: 12px;
}

.view-option-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  cursor: pointer;
  font-size: 0.9rem;
  color: #374151;
  transition: all 0.15s ease;
}

.view-option-btn:hover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.view-option-btn.active {
  border-color: #3b82f6;
  background: #dbeafe;
  color: #1d4ed8;
  font-weight: 600;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
}

.btn-primary:hover {
  background: #2563eb;
}

.calendar-resync-btn:disabled {
  opacity: 0.6;
  cursor: wait;
}

.calendar-popup--hint .calendar-header {
  position: relative;
  z-index: 40;
}

.calendar-resync-hint {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  background: rgba(20, 16, 24, 0.82);
}

.calendar-resync-hint-callout {
  position: absolute;
  top: var(--calendar-resync-hint-top, 0px);
  right: var(--calendar-resync-arrow-right, 13px);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  max-width: calc(100% - var(--calendar-resync-arrow-right, 13px) - 12px);
  color: #fff;
  pointer-events: none;
}

.calendar-resync-hint-arrow {
  width: 40px;
  height: 64px;
  overflow: visible;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.35));
}

.calendar-resync-hint-text {
  margin: 8px 0 0;
  color: #fff;
  font-size: 1.05em;
  font-weight: 700;
  line-height: 1.35;
  text-align: right;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
}

.calendar-resync-hint-dismiss {
  pointer-events: auto;
  margin-top: 1.6em;
  min-height: 44px;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.75);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-size: 0.85em;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
}

/*
 * /calendar from /mhome sits in the 440px phone column on a wide desktop window.
 * Only the width changes. The card keeps its own height and stays centered.
 * These rules are min-width 769px, so a phone still uses the layout below.
 */
@media screen and (min-width: 769px) {
  .calendar-popup.calendar-popup--shell {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: auto;
    height: auto;
  }

  .calendar-popup--shell .calendar-header h3 {
    display: none;
  }

  .calendar-popup--shell .calendar-header-controls {
    margin-left: auto;
  }

  .calendar-popup--shell .calendar-content,
  .calendar-popup--shell .calendar-content.with-events-panel {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    height: auto;
  }

  .calendar-popup--shell .calendar-section {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    margin-left: 0 !important;
  }

  .calendar-popup--shell .calendar-nav,
  .calendar-popup--shell .calendar-grid,
  .calendar-popup--shell .calendar-weekdays,
  .calendar-popup--shell .calendar-footer {
    max-width: 100%;
    margin-left: 0;
  }

  .calendar-popup--shell .events-panel {
    left: 0;
    width: 100%;
    max-width: 100%;
    transform: none;
  }

  .calendar-popup--shell .list-overlay,
  .calendar-popup--shell .grid-overlay,
  .calendar-popup--shell .modal-overlay {
    position: absolute;
  }

  .calendar-popup--shell .list-modal,
  .calendar-popup--shell .grid-modal,
  .calendar-popup--shell .delete-confirmation-modal,
  .calendar-popup--shell .grid-modal-wrapper {
    max-width: 100%;
  }
}
</style>
